import React, { useRef, useEffect, useState } from 'react';

/**
 * Rich email editor — what you'd expect while writing an actual email.
 *
 * Font / size / colour, bold-italic-underline, alignment, lists, links, images
 * and tables. Paste keeps whatever you pasted: formatted text arrives as HTML
 * (bold stays bold), and a pasted image is embedded as a data URL so it still
 * exists when the mail is sent — clipboard images are otherwise `blob:` refs
 * that die with the page.
 */

export const EDITOR_FONTS = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Calibri, sans-serif', label: 'Calibri' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' }
];
export const EDITOR_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px'];

/** Table HTML with inline borders — email clients ignore <style> blocks. */
export const buildTableHtml = (rows, cols) => {
  const cell = '<td style="border:1px solid #cccccc;padding:6px 8px;vertical-align:top;">&nbsp;</td>';
  const head = '<th style="border:1px solid #cccccc;padding:6px 8px;background:#f3f4f6;text-align:left;">&nbsp;</th>';
  const headRow = `<tr>${head.repeat(cols)}</tr>`;
  const bodyRow = `<tr>${cell.repeat(cols)}</tr>`;
  return (
    `<table style="border-collapse:collapse;width:100%;margin:8px 0;font-family:inherit;font-size:inherit;">` +
    `<tbody>${headRow}${bodyRow.repeat(Math.max(0, rows - 1))}</tbody></table><p><br></p>`
  );
};

/** Insert HTML at the caret, falling back to appending. */
export const insertHtmlAtCaret = (editorEl, html) => {
  if (!editorEl) return;
  editorEl.focus();
  const selection = window.getSelection();
  if (selection && selection.rangeCount && editorEl.contains(selection.getRangeAt(0).commonAncestorContainer)) {
    document.execCommand('insertHTML', false, html);
    return;
  }
  editorEl.insertAdjacentHTML('beforeend', html);
};





/**
 * Spell out list formatting before an email goes out.
 *
 * `<ul><li>` is enough in a browser, but mail clients vary: some apply their own
 * reset, and Outlook's Word engine is happier with the marker and indent stated
 * outright. Writing them inline means the bullets that show in the editor are
 * the bullets that arrive.
 */
/**
 * One dot per bullet.
 *
 * Copy bullets out of Word, Outlook or Google Docs and the marker often comes
 * along as a LITERAL CHARACTER inside the item — "• Off-Hours Deep Analysis" —
 * because the source drew its own bullet rather than letting the list do it.
 * The item is still a real <li>, so once we hand the list back its marker (the
 * pass below) the reader gets two: the one the client draws and the one sitting
 * in the text. That is the "dots are twice" the team reported.
 *
 * The other half of the same complaint — "it doesn't show the dots but it sends
 * them" — is the mirror image: a pasted <li> carrying `list-style-type: none`
 * inline shows nothing in the composer, and then the send restores the marker.
 * Both are settled by normalising the paste itself, so the composer and the
 * inbox agree.
 *
 * Only real bullet glyphs are stripped, and only at the very start of an item
 * inside a real list. A hyphen or an asterisk is left alone: those are things
 * people legitimately write.
 */
const LITERAL_BULLET = /^[\s\u00A0]*[\u2022\u00B7\u25CF\u25CB\u25AA\u25AB\u25E6\u2023\u2219\u00BB\uF0B7\uF0A7\uF076][\s\u00A0\t]*/;

export const stripDuplicateBulletsIn = (root) => {
  if (!root) return 0;
  let stripped = 0;
  root.querySelectorAll('li').forEach(li => {
    const list = li.parentElement;
    if (!list || (list.tagName !== 'UL' && list.tagName !== 'OL')) return;
    // If nothing is going to be drawn in its place, the character IS the
    // bullet — leave it. Removing it would take the last visible marker away,
    // which is worse than the double it was meant to prevent.
    const markerOf = (el) => (/list-style-type:\s*([a-z-]+)/i.exec(el.getAttribute('style') || '') || [])[1];
    const marker = (markerOf(li) || markerOf(list) || '').toLowerCase();
    const hidden = marker === 'none'
      || /display:\s*(block|inline)/i.test(li.getAttribute('style') || '');
    if (hidden) return;
    // The first text with anything in it — the glyph is often wrapped in its
    // own <span style="font-family: Symbol">, which Word writes.
    const walker = li.ownerDocument.createTreeWalker(li, 4 /* TEXT */);
    let node = walker.nextNode();
    while (node && !node.nodeValue.replace(/[\s\u00A0]/g, '')) node = walker.nextNode();
    if (!node) return;
    const trimmed = node.nodeValue.replace(LITERAL_BULLET, '');
    if (trimmed === node.nodeValue) return;
    node.nodeValue = trimmed;
    stripped += 1;
    // The wrapper the glyph lived in is now empty; leave no hollow span behind.
    let parent = node.parentElement;
    while (parent && parent !== li && !parent.textContent.replace(/[\s\u00A0]/g, '') && !parent.querySelector('img, table, br')) {
      const next = parent.parentElement;
      parent.remove();
      parent = next;
    }
  });
  return stripped;
};

export const inlineListStylesIn = (root) => {
  if (!root) return;
  const depthOf = (el, tag) => {
    let depth = 0;
    let parent = el.parentElement;
    while (parent && parent !== root) {
      if (parent.tagName === tag) depth += 1;
      parent = parent.parentElement;
    }
    return depth;
  };
  const UL_MARKERS = ['disc', 'circle', 'square'];
  const HAS_INDENT = /padding-left|margin-left/i;

  // `padding-inline-start` is what Google Docs writes, and Outlook's Word engine
  // does not know it — so the indent vanished there while we ALSO added 40px of
  // our own on top of it everywhere else. Say it once, in a property every
  // client understands.
  const settleIndent = (list) => {
    const style = list.getAttribute('style') || '';
    const inlineStart = /padding-inline-start:\s*([^;]+)/i.exec(style);
    if (inlineStart && !/padding-left/i.test(style)) list.style.paddingLeft = inlineStart[1].trim();
    if (inlineStart) list.style.removeProperty('padding-inline-start');
    if (!HAS_INDENT.test(list.getAttribute('style') || '')) list.style.paddingLeft = '40px';
  };

  root.querySelectorAll('ul').forEach(ul => {
    if (!/list-style/i.test(ul.getAttribute('style') || '')) {
      ul.style.listStyleType = UL_MARKERS[Math.min(depthOf(ul, 'UL'), UL_MARKERS.length - 1)];
      ul.style.listStylePosition = 'outside';
    }
    settleIndent(ul);
  });
  root.querySelectorAll('ol').forEach(ol => {
    if (!/list-style/i.test(ol.getAttribute('style') || '')) {
      ol.style.listStyleType = 'decimal';
      ol.style.listStylePosition = 'outside';
    }
    settleIndent(ol);
  });
  root.querySelectorAll('li').forEach(li => {
    // `display: block` on a list item means NO MARKER — anywhere, in any client.
    // Word Online writes exactly that on every <li> and draws its own bullet
    // from a stylesheet class that is not on the clipboard, so its lists
    // arrived as plain indented lines with the dots simply missing. An item in
    // a real list is a list item; `none` is left alone, because that is
    // somebody deliberately hiding a row.
    const declaredDisplay = /display:\s*([a-z-]+)/i.exec(li.getAttribute('style') || '');
    const display = declaredDisplay ? declaredDisplay[1].toLowerCase() : '';
    if (!display || display === 'block' || display === 'inline' || display === 'inline-block') {
      li.style.display = 'list-item';
    }

    // Google Docs writes `list-style-type: none` on EVERY <li> and draws the
    // bullet from its own stylesheet (a `lst-kix_…` class), which is not part
    // of what gets copied. Pasted, the markers are simply gone: the bullets on
    // the pitch template arrived as orphan lines indented 72px — 48px of
    // padding on the <ul> plus 18pt of margin on each <li>. Hand the item back
    // the marker its list is drawing, and drop the second indent.
    const list = li.parentElement;
    if (!list || (list.tagName !== 'UL' && list.tagName !== 'OL')) return;
    const style = li.getAttribute('style') || '';
    if (/list-style-type:\s*none/i.test(style)) {
      li.style.listStyleType = list.style.listStyleType || (list.tagName === 'OL' ? 'decimal' : 'disc');
    }
    if (/margin-left/i.test(style) && HAS_INDENT.test(list.getAttribute('style') || '')) {
      li.style.removeProperty('margin-left');
    }
  });

  // LAST, once every marker above is settled: a marker typed into the text is
  // now a double. Running it earlier meant judging "will a marker be drawn?"
  // before the answer existed — and an item still marked `none` at that moment
  // kept its glyph and then gained a marker too, which is the double all over
  // again.
  stripDuplicateBulletsIn(root);
};

export const inlineListStyles = (html) => {
  if (!html || typeof html !== 'string' || !/<(ul|ol|li)\b/i.test(html)) return html || '';
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return html;
  inlineListStylesIn(root);
  return root.innerHTML;
};

/**
 * Stop images bursting out of their container.
 *
 * An <img> with no width/height renders at its intrinsic size — a 1280px logo
 * blows a signature apart in a mail client. Anything already sized is left
 * exactly as it is; anything unsized simply gets capped at the width of
 * whatever box it sits in.
 */
export const constrainImagesIn = (root) => {
  if (!root) return;
  root.querySelectorAll('img').forEach(img => {
    // Our editors run inside Tailwind, whose reset is `max-width: 100%;
    // height: auto` on every image — so a 900px signature banner is drawn at the
    // width of the composer while a mail client, which has no such rule, shows
    // it at 900px and pushes the message sideways. Say the same thing inline and
    // the two agree. An image that states its own size still wins: an inline
    // declaration is exactly what Tailwind's reset does not override either.
    const declared = img.getAttribute('style') || '';
    if (!/(^|;)\s*max-width\s*:/i.test(declared)) img.style.maxWidth = '100%';
    if (!/(^|;)\s*height\s*:/i.test(declared)) img.style.height = 'auto';
  });
};

export const constrainImages = (html) => {
  if (!html || typeof html !== 'string' || !/<img/i.test(html)) return html || '';
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return html;
  constrainImagesIn(root);
  return root.innerHTML;
};


/* ------------------------------------------------------------------ *
 * Arranging images in an email
 *
 * Email clients ignore flexbox, grid and float. What they DO honour is a
 * paragraph's text-align and images sitting next to each other on one line.
 * These helpers work in those terms, so what you arrange here is what the
 * recipient sees.
 * ------------------------------------------------------------------ */

const BLOCK_TAGS = 'P,DIV,LI,TD,TH,H1,H2,H3,H4,H5,H6,BLOCKQUOTE';

/**
 * Hyperlinking an image.
 *
 * `document.execCommand('createLink')` needs a TEXT selection, so it can never
 * link a picture — which is why the LinkedIn and Instagram badges in a
 * signature could not be pointed anywhere. Wrapping the <img> in an <a> is what
 * every mail client understands, so that is what these do.
 */
export const imageLinkOf = (img) => {
  if (!img) return null;
  // In the editor the picture also sits inside a .image-wrapper (the drag and
  // resize affordance). Step over those, or a linked image looks unlinked the
  // moment the editor re-renders.
  let el = img.parentElement;
  while (el && el.classList && el.classList.contains('image-wrapper')) el = el.parentElement;
  return el && el.tagName === 'A' ? el : null;
};

export const imageLinkHref = (img) => imageLinkOf(img)?.getAttribute('href') || '';

/** Point an image at a URL. Passing an empty URL removes the link. */
export const linkImage = (img, url) => {
  if (!img) return false;
  const href = String(url || '').trim();
  const existing = imageLinkOf(img);
  if (!href) {
    if (existing) existing.replaceWith(...existing.childNodes);
    return true;
  }
  const full = /^(https?:|mailto:|tel:)/i.test(href) ? href : `https://${href}`;
  if (existing) {
    existing.setAttribute('href', full);
    existing.setAttribute('target', '_blank');
    existing.setAttribute('rel', 'noopener noreferrer');
    return true;
  }
  const a = (img.ownerDocument || document).createElement('a');
  a.setAttribute('href', full);
  a.setAttribute('target', '_blank');
  a.setAttribute('rel', 'noopener noreferrer');
  // No underline or colour on a linked picture — mail clients otherwise draw a
  // blue box around the badge.
  a.style.textDecoration = 'none';
  a.style.border = 'none';
  img.parentNode.insertBefore(a, img);
  a.appendChild(img);
  // Outlook draws its own border around a linked image unless told not to.
  img.setAttribute('border', '0');
  img.style.border = 'none';
  return true;
};

/**
 * What actually gets MOVED when an image is rearranged.
 *
 * A hyperlinked image lives inside its <a>. Moving the bare <img> would leave
 * the anchor behind and silently drop the link, so every arrange helper moves
 * this instead.
 */
export const movableOf = (img) => imageLinkOf(img) || img;

/** The block element an image sits in — that is what carries the alignment. */
export const blockOf = (node, root) => {
  let el = node;
  while (el && el !== root) {
    if (el.nodeType === 1 && BLOCK_TAGS.includes(el.tagName)) return el;
    el = el.parentElement;
  }
  return null;
};

/** Left / centre / right, applied to the image's own line. */
export const alignImage = (img, direction, root) => {
  const block = blockOf(img, root);
  if (!block) return;
  block.style.textAlign = direction;
  // A centred image must not also be floated or displayed as a block-with-margin
  clearStyle(img, 'float');
  if ((img.style.display || '').toLowerCase() === 'block') {
    img.style.display = 'inline-block';
  }
};

/** Put this image on the same line as whatever is above it (side by side). */
export const joinPreviousLine = (img, root) => {
  const block = blockOf(img, root);
  if (!block) return false;
  const previous = block.previousElementSibling;
  if (!previous || !BLOCK_TAGS.includes(previous.tagName)) return false;
  // Inline images sit next to each other; a block one never will.
  img.style.display = 'inline-block';
  img.style.verticalAlign = img.style.verticalAlign || 'middle';
  previous.appendChild(document.createTextNode(' '));
  previous.appendChild(movableOf(img));
  if (!block.textContent.trim() && !block.querySelector('img')) block.remove();
  return true;
};

/** Give this image a line of its own again. */
export const moveToOwnLine = (img, root) => {
  const block = blockOf(img, root);
  if (!block) return false;
  const movable = movableOf(img);
  if (block.childNodes.length === 1 && block.firstChild === movable) return true; // already alone
  const line = document.createElement('p');
  line.style.margin = '4px 0';
  block.parentNode.insertBefore(line, block.nextSibling);
  img.style.display = 'inline-block';
  line.appendChild(movable);
  return true;
};

/** Width in pixels, on both the style and the attributes Outlook reads. */
export const setImageWidth = (img, width) => {
  const px = Math.max(8, Math.round(Number(width) || 0));
  if (!px) return;
  const ratio = (img.naturalWidth && img.naturalHeight) ? img.naturalHeight / img.naturalWidth : null;
  img.setAttribute('width', String(px));
  img.style.width = `${px}px`;
  if (ratio) {
    const height = Math.round(px * ratio);
    img.setAttribute('height', String(height));
    img.style.height = `${height}px`;
  }
  img.style.maxWidth = '100%';
  return px;
};

/**
 * Turn a run of images into a single row, side by side, using a table.
 * Tables are the one layout primitive every mail client renders the same way —
 * this is exactly how Outlook builds its own signatures.
 */
export const rowFromImages = (images, spacing = 10) => {
  if (!images.length) return null;
  const table = document.createElement('table');
  table.setAttribute('cellpadding', '0');
  table.setAttribute('cellspacing', '0');
  table.style.borderCollapse = 'collapse';
  table.style.display = 'inline-table';
  const tbody = document.createElement('tbody');
  const tr = document.createElement('tr');
  images.forEach((img, i) => {
    const td = document.createElement('td');
    td.style.verticalAlign = 'middle';
    if (i < images.length - 1) td.style.paddingRight = `${spacing}px`;
    img.style.display = 'inline-block';
    td.appendChild(movableOf(img));
    tr.appendChild(td);
  });
  tbody.appendChild(tr);
  table.appendChild(tbody);
  return table;
};

/**
 * Collect this image and the image-only lines below it into ONE row.
 *
 * This is the signature case: a strip of badges — LinkedIn, Instagram, the ISO
 * marks — that belongs side by side but arrives from the clipboard as one image
 * per line. Click the first badge, press the button, and they become a single
 * table row: the layout every mail client renders identically, and the same
 * structure Outlook itself uses for signatures.
 *
 * Returns how many images were gathered.
 */
/**
 * Remove an inline style property outright.
 *
 * Assigning '' is supposed to clear a declaration, but it is not reliable
 * everywhere (jsdom keeps the old value, which is how a stale gap survived a
 * reorder). removeProperty says exactly what we mean.
 */
const clearStyle = (el, ...properties) => {
  if (!el || !el.style) return;
  properties.forEach(prop => {
    try { el.style.removeProperty(prop); } catch { el.style[prop] = ''; }
  });
  if (el.getAttribute && !el.getAttribute('style')) el.removeAttribute('style');
};

/** Even gaps between the badges: every cell but the last carries the spacing. */
export const normaliseRowSpacing = (row, spacing = 10) => {
  if (!row) return;
  const cells = [...row.children].filter(c => /^(TD|TH)$/.test(c.tagName));
  cells.forEach((cell, i) => {
    if (i < cells.length - 1) cell.style.paddingRight = `${spacing}px`;
    else clearStyle(cell, 'padding-right');
  });
};

/**
 * Shuffle an image one place left or right.
 *
 * Inside a row, the image's whole CELL swaps with its neighbour, so the spacing
 * and alignment travel with it. Loose on a line, the image simply swaps with the
 * image next to it. Returns false when it is already at the end — nothing to do.
 */
export const moveImage = (img, direction, root, spacing = 10) => {
  if (!img) return false;
  const goLeft = direction === 'left';

  const cell = typeof img.closest === 'function' ? img.closest('td, th') : null;
  if (cell && cell.parentElement) {
    const row = cell.parentElement;
    const neighbour = goLeft ? cell.previousElementSibling : cell.nextElementSibling;
    if (!neighbour) return false;
    if (goLeft) row.insertBefore(cell, neighbour);
    else row.insertBefore(neighbour, cell);
    normaliseRowSpacing(row, spacing);
    return true;
  }

  // Not in a row: reorder among the images sharing this line.
  const block = blockOf(img, root);
  if (!block) return false;
  const images = [...block.querySelectorAll('img')];
  const index = images.indexOf(img);
  const target = goLeft ? images[index - 1] : images[index + 1];
  if (!target) return false;
  // Move the anchor, not the picture inside it, or the link is left behind.
  const self = movableOf(img);
  const other = movableOf(target);
  if (goLeft) block.insertBefore(self, other);
  else block.insertBefore(other, self);
  return true;
};

/** The table around the caret, or the only table in the body. */
export const tableFromSelection = (root) => {
  if (!root) return null;
  if (typeof window !== 'undefined') {
    const selection = window.getSelection();
    const node = selection && selection.anchorNode;
    if (node) {
      const el = node.nodeType === 1 ? node : node.parentElement;
      const table = el && typeof el.closest === 'function' ? el.closest('table') : null;
      if (table && root.contains(table)) return table;
    }
  }
  const tables = root.querySelectorAll('table');
  return tables.length === 1 ? tables[0] : null;
};

/** Does this table draw any lines at all? */
export const tableHasBorders = (table) => {
  if (!table) return false;
  const parts = [table, ...table.querySelectorAll('td, th, tr')];
  return parts.some(el => {
    const css = el.getAttribute('style') || '';
    // A width with no style draws nothing, so only count real, visible edges.
    return /border[a-z-]*\s*:\s*[^;]*\b(solid|dashed|dotted|double|groove|ridge)\b/i.test(css)
      || (el.getAttribute('border') && el.getAttribute('border') !== '0');
  });
};

/**
 * Turn a table's lines on or off.
 *
 * A signature pasted out of Word carries per-cell borders — often uneven ones,
 * which is why the delivered mail showed a half-drawn box. This clears every
 * border declaration on the table, its rows and its cells (or puts a light,
 * even one back), leaving spacing and widths alone.
 */
export const setTableBorders = (table, on, colour = '#cccccc') => {
  if (!table) return false;
  const parts = [table, ...table.querySelectorAll('tr, td, th')];
  parts.forEach(el => {
    const style = el.getAttribute('style') || '';
    const kept = style.split(';').map(d => d.trim()).filter(Boolean)
      .filter(d => !/^border/i.test(d.split(':')[0].trim()));
    if (on && /^(TD|TH)$/.test(el.tagName)) kept.push(`border: 1px solid ${colour}`);
    if (kept.length) el.setAttribute('style', kept.join('; '));
    else el.removeAttribute('style');
    if (el.hasAttribute && el.hasAttribute('border')) el.setAttribute('border', on ? '1' : '0');
  });
  if (on) table.style.borderCollapse = 'collapse';
  return true;
};

/** Every image the user has highlighted, in document order. */
export const imagesInSelection = (root) => {
  if (!root || typeof window === 'undefined') return [];
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return [];
  const range = selection.getRangeAt(0);
  return [...root.querySelectorAll('img')].filter(img => {
    try {
      if (typeof selection.containsNode === 'function') return selection.containsNode(img, true);
      return range.intersectsNode(img);
    } catch {
      return false;
    }
  });
};

/**
 * Put an explicit list of images into ONE row, wherever they currently live.
 *
 * The row is a table — the only layout every mail client renders the same way,
 * and what Outlook itself uses for signatures. The images are MOVED into it, so
 * whatever lines they came from are cleaned up if nothing is left on them.
 *
 * Returns how many images ended up in the row.
 */
export const makeRowFromImageList = (images, root, spacing = 10) => {
  const list = [...new Set((images || []).filter(Boolean))];
  if (list.length < 2) return list.length;

  // Document order, so the row reads the way it looked going down the page.
  list.sort((a, b) => (
    a.compareDocumentPosition(b) & (typeof Node !== 'undefined' ? Node.DOCUMENT_POSITION_FOLLOWING : 4)
  ) ? -1 : 1);

  // Capture the lines BEFORE moving anything — afterwards the images have moved.
  const blocks = list.map(img => blockOf(img, root)).filter(Boolean);
  const firstBlock = blocks[0] || list[0].parentElement;
  if (!firstBlock || !firstBlock.parentNode) return 0;

  // If these images already live in a table (a row being rebuilt), the new row
  // goes where that TABLE is — dropping it inside the old cell would nest a
  // table in a table and leave the emptied cells behind.
  const inCell = firstBlock.tagName === 'TD' || firstBlock.tagName === 'TH';
  const oldTables = [...new Set(blocks.map(b => b.closest && b.closest('table')).filter(Boolean))];
  const anchor = inCell ? (firstBlock.closest('table') || firstBlock) : firstBlock;
  if (!anchor.parentNode) return 0;

  const table = rowFromImages(list, spacing);
  const holder = inCell ? document.createElement('p') : firstBlock.cloneNode(false);
  holder.innerHTML = '';
  holder.appendChild(table);
  anchor.parentNode.insertBefore(holder, anchor);

  const isEmpty = (el) => !el.querySelector('img, table')
    && !el.textContent.replace(/\u00a0/g, ' ').trim();

  // Drop any line the images left empty; keep lines that still hold text.
  [...new Set(blocks)].forEach(block => {
    if (block === holder || !block.parentNode) return;
    if (isEmpty(block)) block.remove();
  });

  // ...and any table those cells belonged to, once it has nothing left in it.
  oldTables.forEach(old => {
    if (old === table || !old.parentNode) return;
    if (isEmpty(old)) old.remove();
  });

  return list.length;
};

/**
 * Click one image and line it up with the image-only lines beneath it.
 * Blank lines in between are stepped over rather than ending the run — a pasted
 * signature often has an empty paragraph between badges.
 */
export const makeRowFromImage = (img, root, spacing = 10) => {
  const startBlock = blockOf(img, root);
  if (!startBlock) return 0;

  const textOf = (el) => (el?.textContent || '').replace(/\u00a0/g, ' ').trim();
  const isImageOnlyLine = (el) => el && el.nodeType === 1 && BLOCK_TAGS.includes(el.tagName)
    && el.querySelector('img') && !textOf(el);
  const isBlankLine = (el) => el && el.nodeType === 1 && BLOCK_TAGS.includes(el.tagName)
    && !el.querySelector('img, table') && !textOf(el);

  const images = [...startBlock.querySelectorAll('img')];
  let next = startBlock.nextElementSibling;
  const skipped = [];
  while (next) {
    if (isImageOnlyLine(next)) {
      images.push(...next.querySelectorAll('img'));
      skipped.length = 0;               // the run continues
      next = next.nextElementSibling;
    } else if (isBlankLine(next)) {
      skipped.push(next);               // might just be spacing between badges
      next = next.nextElementSibling;
    } else {
      break;
    }
  }
  if (images.length < 2) return images.length;
  skipped.forEach(el => el.remove());
  return makeRowFromImageList(images, root, spacing);
};

/**
 * Undo the double gap in bodies this app itself generated.
 *
 * The plain-text renderer used to emit BOTH a 0.75em bottom margin on every
 * paragraph AND a `&nbsp;` spacer for the blank line the writer typed between
 * them — so a normal template arrived in the inbox at roughly three times the
 * spacing it was written with. The renderer no longer does that, but bodies
 * saved before the fix still carry the extra spacers, and one of those is the
 * pitch every BDA sends.
 *
 * Only OUR OWN markup is touched: an empty paragraph whose margin is exactly
 * the spacer we wrote (0.75em above it, 0.5em on itself). Pasted Word or
 * Outlook spacing has neither signature and is left completely alone. Running
 * this on an already-corrected body is a no-op.
 */
// Both gaps are stated in PIXELS, and that is not cosmetic. Classic Outlook for
// Windows draws mail with Word's engine, and Word does not understand `em` at
// all — it drops the declaration rather than approximating it. Every paragraph
// gap in this app used to be `0.75em`, so the same message that looked right in
// Gmail arrived in Outlook with no space between paragraphs whatsoever: one
// solid wall of text. Pixels are the unit both engines read the same way.
const SPACER_MARGIN = '8px';
const PARAGRAPH_MARGIN = '12px';
// What we wrote before the switch. Bodies saved then are still in the database
// — every template a BDA sends — so both forms have to be recognised here, and
// `makeWordSafeIn` converts the old ones on the way out.
const LEGACY_SPACER_MARGIN = '0.5em';
const LEGACY_PARAGRAPH_MARGIN = '0.75em';
const isGeneratedSpacer = (el) => el && el.tagName === 'P'
  && (el.style.marginBottom === SPACER_MARGIN || el.style.marginBottom === LEGACY_SPACER_MARGIN)
  && (el.style.marginTop === '0px' || el.style.marginTop === '0')
  && !el.querySelector('img, table')
  && !el.textContent.replace(/\u00a0/g, ' ').trim();
const isGeneratedParagraph = (el) => el && el.tagName === 'P'
  && (el.style.marginBottom === PARAGRAPH_MARGIN || el.style.marginBottom === LEGACY_PARAGRAPH_MARGIN);

export const collapseGeneratedSpacersIn = (root) => {
  if (!root) return;
  Array.from(root.querySelectorAll('p')).forEach(p => {
    if (!isGeneratedSpacer(p)) return;
    const before = p.previousElementSibling;
    // Only the FIRST spacer of a run goes: it is the one the paragraph margin
    // already draws. A writer who left two blank lines still gets one.
    if (isGeneratedParagraph(before)) p.remove();
  });
};

/**
 * One vertical rhythm for the whole message body.
 *
 * A template is almost never written in one place, and the same body ends up
 * holding paragraphs from three sources, each with its own idea of a gap:
 *
 *   - typed here:        <p style="margin:0 0 0.75em 0">            → 10.5px
 *   - Google Docs/Word:  <p style="margin:0">, with the blank line the writer
 *                        typed sitting between them as a real empty paragraph
 *                                                                   → 19px
 *   - a bare <p> with no style at all, which takes the mail client's DEFAULT
 *     1em above and below                                           → 14px,
 *     and about 49px around an empty one
 *
 * Measured on the live "Follow-up 1" template the gaps ran 10.5, 14, 14, 24.5,
 * 14, 49 — every one different. That is what "the spacing is weird" actually
 * is, and it is why the earlier passes did not settle it: each of them removed
 * one SOURCE of extra space, none of them made the finished body consistent.
 *
 * So state the gap on every body block, and fold the blank-line idiom into it:
 * a run of empty separator blocks loses its first member (the one the margin
 * now draws) and keeps the rest as deliberate empty lines. Two <br>s in a row
 * are the same idiom written inline, so they become a paragraph break too.
 *
 * Signatures are laid out in tables and list spacing belongs to the list, so
 * neither is touched.
 *
 * Idempotent: afterwards every body block carries the margin, every kept blank
 * line is marked as already decided, and no run of two <br>s is left.
 */
const KEPT_GAP = 'data-ags-gap';
// A wrapper's margin is not the paragraph gap — only leaf blocks are paragraphs.
const NESTED_BLOCK = 'p, div, ul, ol, table, blockquote, h1, h2, h3, h4, h5, h6';

// A whole list is one body block: it needs the gap under it like a paragraph
// does, or the next heading hugs the last bullet. The items inside it do not —
// bullet-to-bullet spacing belongs to the list.
const BODY_BLOCK_TAGS = 'p, div, ul, ol, h1, h2, h3, h4, h5, h6';

const isBodyBlock = (el, root) => el !== root
  // A signature is a table and owns its own layout; a list item owns its own.
  && !el.closest('table, li, blockquote')
  && !el.querySelector(NESTED_BLOCK);

const isSeparatorBlock = (el) => !el.querySelector('img, table, svg, hr')
  && !el.textContent.replace(/ /g, ' ').trim();

// Undeclared, and "0" in any unit, both mean "no gap of its own".
const isZeroLength = (value) => !value || /^0(px|pt|em|rem|cm|mm|in|pc|ex|%)?$/i.test(value.trim());

// The block's content in document order, ignoring whitespace-only text.
const inlineFlowOf = (block) => {
  const walker = block.ownerDocument.createTreeWalker(block, 1 | 4 /* ELEMENT | TEXT */);
  const flow = [];
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === 3) {
      if (node.nodeValue.replace(/ /g, ' ').trim()) flow.push(node);
    } else if (node.tagName === 'BR' || node.tagName === 'IMG' || node.tagName === 'TABLE') {
      flow.push(node);
    }
    node = walker.nextNode();
  }
  return flow;
};

// Cut a block in two after `node`, keeping the inline formatting on both sides
// — extractContents clones whatever spans the cut passes through.
const splitBlockAfter = (block, node) => {
  const range = block.ownerDocument.createRange();
  range.setStartAfter(node);
  range.setEnd(block, block.childNodes.length);
  const tail = block.cloneNode(false);
  tail.appendChild(range.extractContents());
  block.parentNode.insertBefore(tail, block.nextSibling);
  return tail;
};

/**
 * A paragraph nobody styled, in a place the rhythm pass cannot reach.
 *
 * `normaliseBlockRhythmIn` deliberately leaves table cells, list items and
 * blockquotes alone — a signature owns its own layout. But a bare <p> inside
 * one still takes the MAIL CLIENT's default margin, a full 1em above AND below,
 * while our editors show it with no margin at all (Tailwind's reset zeroes it).
 * Measured in Chrome on the real stored templates, that is 14-16px of space per
 * paragraph that the composer never showed: "Rithika's Template" ran four gaps
 * of 0px on screen and 15px in the inbox, and the pitch arrived 212px shorter
 * than it looked while it was being written.
 *
 * So state it. Nothing that carries a margin of its own is touched; everything
 * else is pinned to the zero the writer was looking at.
 *
 * Idempotent: afterwards every such block declares its own margin.
 */
/**
 * Markup that means the same thing after it is read back.
 *
 * Outlook wraps a pasted signature as `<p class=MsoNormal><table>…</table></p>`,
 * which is not legal HTML: a paragraph cannot hold a table. Every parser —
 * ours, Gmail's, Outlook's — repairs it the same way, by closing the paragraph
 * before the table and opening a fresh one after it. That leftover `<p></p>`
 * carries no styling of ours, so it takes the mail client's default 1em above
 * AND below: about 30px of blank space under the signature that the composer
 * never showed, because the composer holds the DOM it built rather than a
 * re-read of it.
 *
 * A paragraph holding block content is really a wrapper, so make it one. Its
 * attributes come along, which keeps whatever margin it declared.
 *
 * Idempotent, and it is what makes the whole render idempotent: afterwards the
 * serialised body parses back to the same tree.
 */
const makeReparseStableIn = (root) => {
  if (!root) return;
  const doc = root.ownerDocument;
  Array.from(root.querySelectorAll('p')).forEach(para => {
    if (!para.querySelector(NESTED_BLOCK)) return;
    const wrapper = doc.createElement('div');
    Array.from(para.attributes).forEach(a => wrapper.setAttribute(a.name, a.value));
    while (para.firstChild) wrapper.appendChild(para.firstChild);
    para.replaceWith(wrapper);
  });
};

const declaresMargin = (el) => Boolean(el.style.marginTop || el.style.marginBottom || el.style.margin);
const pinNestedBlockMarginsIn = (root) => {
  if (!root) return;
  // A blockquote is quoted text, and the client's own idea of it (1em 40px) is
  // not the one the editor shows (0.5em 0 0.5em 1em, from index.css).
  root.querySelectorAll('blockquote').forEach(quote => {
    if (!declaresMargin(quote)) quote.style.margin = '8px 0 8px 16px';
  });
  // Everything the rhythm pass left alone — inside a signature table, a list
  // item or a wrapper it does not treat as a paragraph. On screen these have no
  // margin at all; say so, and the mail client cannot add one.
  root.querySelectorAll('p, div, ul, ol, h1, h2, h3, h4, h5, h6').forEach(block => {
    // Not the surface itself: it is the message, not a block inside it, and
    // writing a margin onto it would make a second render differ from the first.
    if (block.hasAttribute(BODY_MARK)) return;
    if (!declaresMargin(block)) block.style.margin = '0';
  });
  // A table lays its own columns out, and the two surfaces disagree about how:
  // our editors run inside Tailwind, which collapses borders and zeroes cell
  // spacing, while a mail client separates them with 2px. The cells then come
  // out a few pixels narrower there, the signature wraps onto an extra line,
  // and the message ends up a line or two longer than the composer showed.
  root.querySelectorAll('table').forEach(table => {
    const declared = table.getAttribute('style') || '';
    if (!/(^|;)\s*border-collapse\s*:/i.test(declared)) table.style.borderCollapse = 'collapse';
    if (!/(^|;)\s*border-spacing\s*:/i.test(declared)) table.style.borderSpacing = '0';
    // Word's engine puts its own margin either side of a table — the reason a
    // signature sits further in, and further down, in classic Outlook than
    // anywhere else. Only Word reads these two.
    if (!/mso-table-lspace/i.test(declared)) {
      const now = (table.getAttribute('style') || '').trim();
      const sep = !now || now.endsWith(';') ? '' : '; ';
      table.setAttribute('style', `${now}${sep}mso-table-lspace: 0pt; mso-table-rspace: 0pt;`);
    }
  });
};

/**
 * Make the finished body legible in classic Outlook for Windows.
 *
 * Outlook on Windows does not use a browser to draw mail — it uses WORD. That
 * engine reads a much smaller CSS vocabulary than Gmail or Apple Mail, and it
 * fails SILENTLY: a declaration it does not understand is dropped, not
 * approximated. Everything below is a declaration we were emitting that Word
 * threw away, measured against the real sent mail the BDAs complained about:
 *
 *   - `em` lengths. Word has no idea what an em is. Every paragraph gap in this
 *     app was `margin-bottom: 0.75em`, so the pitch that reads correctly in
 *     Gmail arrived on Windows with ZERO space between paragraphs — the "all
 *     crammed together" the whole team was seeing. Pixels are read by both.
 *   - unitless `line-height`. `1.5` means nothing to Word either; it falls back
 *     to single spacing, which is the other half of the cramping. `150%` is the
 *     same instruction in a form Word obeys.
 *   - `transform: scale()`. Outlook Web scales a pasted signature table to fit
 *     and records it as a transform. Word ignores transforms, so the signature
 *     is drawn at its UNSCALED width — wider than the reading pane, which is
 *     why the message had a horizontal scrollbar. Baking the factor into the
 *     widths says the same thing in a way every client honours.
 *   - fractional and CSS-only table widths (`width: 826.733px`). Word rounds
 *     each cell its own way and lays the table out from the content when the
 *     width is CSS alone, so it creeps wider than intended. Integer widths,
 *     mirrored onto the `width` ATTRIBUTE, are what Word actually measures.
 *   - image sizes given only in CSS. Same story: the `width`/`height`
 *     attributes are what Word sizes a picture from, so a logo styled purely in
 *     CSS came out at its full original size and burst out of its cell.
 *
 * Nothing here changes how the message looks anywhere else — every rewrite is
 * the same measurement said in a second dialect. Idempotent: pixels stay
 * pixels, a percentage stays a percentage, and a baked transform is gone.
 */
const EM_LENGTH = /(-?[\d.]+)\s*r?em\b/gi;
const UNITLESS_NUMBER = /^\d*\.?\d+$/;

/** `1.5` -> `150%`: the same line spacing, in the dialect Word reads. */
export const asMailLineHeight = (value) => {
  const text = String(value == null ? '' : value).trim();
  return UNITLESS_NUMBER.test(text) ? `${Math.round(parseFloat(text) * 100)}%` : text;
};

// A CSS length in the units a pasted signature actually uses, as pixels.
const pxLengthOf = (value) => {
  const m = /^(-?[\d.]+)\s*(px|pt|in|cm|mm|pc)?$/i.exec(String(value || '').trim());
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n)) return null;
  const unit = (m[2] || 'px').toLowerCase();
  const perUnit = { px: 1, pt: 96 / 72, in: 96, cm: 96 / 2.54, mm: 96 / 25.4, pc: 16 };
  return n * perUnit[unit];
};

/**
 * Edit an element's inline styles as TEXT, one declaration at a time.
 *
 * Not `el.style.x = y`, and that is deliberate. Touching the CSSOM makes the
 * browser rewrite the whole style attribute from the declarations it managed to
 * parse — and it does not parse `mso-table-lspace` or `mso-line-height-rule`,
 * the two directives that exist purely to tell Word what to do. Setting one
 * pixel width through the CSSOM would therefore silently delete the Outlook
 * fixes sitting beside it. Working on the string keeps everything we did not
 * ask to change, in the order it was written.
 *
 * `edit(property, value)` returns the new value, null to drop the declaration,
 * or undefined to leave it exactly as it was written. Data URIs are held aside
 * first: a base64 payload can contain both semicolons and the letters "em",
 * and neither is CSS.
 */
const patchStyleAttribute = (el, edit) => {
  const declared = el.getAttribute('style');
  if (!declared) return;
  const urls = [];
  const masked = declared.replace(/url\(([^)]*)\)/gi, (match) => {
    urls.push(match);
    return `url(\u0000${urls.length - 1}\u0000)`;
  });
  let changed = false;
  const kept = masked.split(';').map(part => {
    if (!part.trim()) return null;
    const at = part.indexOf(':');
    if (at < 0) return part;
    const property = part.slice(0, at).trim().toLowerCase();
    const value = part.slice(at + 1).trim();
    const next = edit(property, value);
    if (next === undefined || next === value) return part;
    changed = true;
    return next === null ? null : `${part.slice(0, at)}: ${next}`;
  }).filter(part => part !== null);
  if (!changed) return;
  const rebuilt = kept.join(';').replace(/url\(\u0000(\d+)\u0000\)/g, (_, i) => urls[Number(i)]);
  const trimmed = rebuilt.trim();
  if (trimmed && trimmed !== ';') el.setAttribute('style', trimmed.endsWith(';') ? trimmed : `${trimmed};`);
  else el.removeAttribute('style');
};

export const makeWordSafeIn = (root, style = DEFAULT_BODY_STYLE) => {
  if (!root) return;
  // An em is relative to the font it sits in, and the surface states that font
  // for the whole message — so that is the number to convert against.
  const base = pxLengthOf(style && style.fontSize) || pxLengthOf(DEFAULT_BODY_STYLE.fontSize) || 14;

  root.querySelectorAll('[style]').forEach(el => {
    const declared = el.getAttribute('style') || '';
    // Cheap gate: the vast majority of elements need nothing doing.
    if (!/[\d.]\s*r?em\b/i.test(declared) && !/line-height/i.test(declared)) return;
    patchStyleAttribute(el, (property, value) => {
      if (property === 'line-height') return asMailLineHeight(value);
      EM_LENGTH.lastIndex = 0;
      if (!EM_LENGTH.test(value)) return value;
      EM_LENGTH.lastIndex = 0;
      return value.replace(EM_LENGTH, (_, n) => `${Math.round(parseFloat(n) * base * 100) / 100}px`);
    });
  });

  // Take `mso-line-height-rule: exactly` back out of bodies that were saved
  // with it. Word reads it as a FIXED line box and inherits it everywhere, so
  // one line of it at the top of the message clipped the tops off every line
  // whose text was taller than its own line-height — which, in a pasted
  // signature, is most of them. Removed, Word treats the line-height as a
  // minimum: honoured, but free to grow for a bigger font.
  root.querySelectorAll('[style*="mso-line-height-rule"]').forEach(el => {
    patchStyleAttribute(el, (property) => (property === 'mso-line-height-rule' ? null : undefined));
  });

  // A picture is sized from its ATTRIBUTES in Word, whatever the CSS says.
  root.querySelectorAll('img').forEach(img => {
    [['width', img.style.width], ['height', img.style.height]].forEach(([attr, css]) => {
      if (img.getAttribute(attr)) return;
      const px = pxLengthOf(css);
      if (px && px > 0) img.setAttribute(attr, String(Math.round(px)));
    });
  });

  root.querySelectorAll('table').forEach(table => {
    // Outlook Web's fit-to-width, which Word cannot see. Fold it into the
    // widths so every client draws the size the sender was looking at.
    const scaled = /scale\(\s*([\d.]+)/.exec(table.style.transform || '');
    const factor = scaled ? parseFloat(scaled[1]) : 1;
    const bake = Boolean(scaled) && Number.isFinite(factor) && factor > 0 && factor !== 1;
    if (scaled) {
      patchStyleAttribute(table, (property) =>
        (property === 'transform' || property === 'transform-origin') ? null : undefined);
    }
    [table, ...table.querySelectorAll('td, th, col')].forEach(cell => {
      const px = pxLengthOf(cell.style.width);
      if (px === null || px <= 0) return;
      const finalPx = Math.round(bake ? px * factor : px);
      patchStyleAttribute(cell, (property, value) =>
        (property === 'width' ? `${finalPx}px` : value));
      // Word measures a table from the attribute, so say it there too.
      if (!cell.getAttribute('width')) cell.setAttribute('width', String(finalPx));
    });
  });
};

/**
 * Say the typography out loud, once, around the whole message.
 *
 * Text that declares no font takes whatever the surface it is laid out on says
 * — and the three surfaces disagree: the Templates editor is Tailwind's
 * `text-sm` (14px/20px), the Send Emails editor inherits the browser's 16px/24px,
 * and a mail client uses its own (Gmail ~13px, Outlook 11pt) with a line-height
 * of `normal`, about 1.2. Same HTML, three different documents. On the real
 * "Initial Sales Pitch" that was 38 paragraphs at 16px on screen and 14px in the
 * inbox, and 1232px of message arriving as 1019px — the composer showing airier
 * text than anyone actually receives.
 *
 * One wrapper stating font, size, line-height and colour settles it: no client
 * default and no app stylesheet reaches the content any more, so what is written
 * is what is delivered. Anything that carries its own styling still wins — this
 * only fills the silence.
 *
 * Idempotent: the wrapper is marked, and a body that already has one is left as
 * it is.
 */
const BODY_MARK = 'data-ags-body';
const wrapBodySurfaceIn = (root, style) => {
  if (!root) return;
  // Already the whole message? Leave it. A marked wrapper deeper in the tree is
  // somebody's pasted-in rendered body, and that still needs a surface of its
  // own around it.
  if (root.children.length === 1 && root.firstElementChild
      && root.firstElementChild.hasAttribute(BODY_MARK)) return;
  const doc = root.ownerDocument;
  const surface = doc.createElement('div');
  surface.setAttribute(BODY_MARK, '1');
  surface.setAttribute('style', [
    `font-family: ${style.fontFamily || DEFAULT_BODY_STYLE.fontFamily}`,
    `font-size: ${style.fontSize || DEFAULT_BODY_STYLE.fontSize}`,
    // A percentage, not the bare `1.5` this used to say: Word (which is what
    // draws mail in Outlook for Windows) ignores a unitless line-height and
    // falls back to single spacing, and that is half of why the same message
    // looked tight and cramped there and correct in Gmail.
    `line-height: ${asMailLineHeight(style.lineHeight || DEFAULT_BODY_STYLE.lineHeight)}`,
    // NO `mso-line-height-rule: exactly` here, and that is deliberate.
    //
    // In Word "exactly" means the line box is FIXED: text taller than it is
    // clipped rather than given room, and Word inherits the rule down the whole
    // message. One directive at the top therefore governs every line below it —
    // including a pasted signature whose paragraphs carry line-heights of
    // 12.8px and 17.6px around 12pt and 14pt text. Those lines cannot fit, so
    // Word cut their tops off, which is the "dirty, cut about" look on the real
    // BESS template (font sizes 8pt/11pt/12pt/14pt/14px/15px in one body).
    //
    // Without the rule Word treats the percentage as a MINIMUM: it uses 150%
    // and grows a line only when the text in it genuinely needs more. Never
    // single-spaced, which was the original complaint, and never clipped.
    `color: ${style.textColor || DEFAULT_BODY_STYLE.textColor}`
  ].join('; ') + ';');
  while (root.firstChild) surface.appendChild(root.firstChild);
  root.appendChild(surface);
};

export const normaliseBlockRhythmIn = (root) => {
  if (!root) return;

  // 1. Two <br>s in a row are a paragraph break someone wrote inline.
  Array.from(root.querySelectorAll('p, div')).forEach(block => {
    if (!isBodyBlock(block, root)) return;
    let current = block;
    for (let guard = 0; guard < 200; guard += 1) {
      const flow = inlineFlowOf(current);
      const at = flow.findIndex((n, i) => n.tagName === 'BR'
        && flow[i + 1] && flow[i + 1].tagName === 'BR');
      if (at < 0) break;
      const [first, second] = [flow[at], flow[at + 1]];
      current = splitBlockAfter(current, second);
      first.remove();
      second.remove();
    }
  });

  // 1b. A <br> hanging off the end of a block was somebody reaching for the gap
  // the margin now draws — and inside a list item it is the one place step 1
  // cannot reach, which is why the last bullet of a section sat lower than the
  // others. Nothing but whitespace follows them, so they only add space.
  Array.from(root.querySelectorAll('p, div, li')).forEach(block => {
    if (block.querySelector(NESTED_BLOCK)) return;
    let last = block.lastChild;
    while (last) {
      const previous = last.previousSibling;
      if (last.nodeType === 3 && !last.nodeValue.replace(/ /g, ' ').trim()) last.remove();
      else if (last.nodeType === 1 && last.tagName === 'BR') last.remove();
      else break;
      last = previous;
    }
  });

  // 2. State the gap on every body block, and turn blank lines into that gap.
  const doomed = [];
  let prevWasSeparator = false;
  let seenText = false;
  Array.from(root.querySelectorAll(BODY_BLOCK_TAGS)).forEach(block => {
    if (!isBodyBlock(block, root)) return;

    if (!isSeparatorBlock(block)) {
      // A body saved when the gap was still `0.75em` is OURS, and Outlook for
      // Windows cannot read that unit — restate it in the pixels both engines
      // understand rather than leaving it as somebody's deliberate measurement.
      if (block.style.marginBottom === LEGACY_PARAGRAPH_MARGIN) {
        block.style.marginTop = '0';
        block.style.marginBottom = PARAGRAPH_MARGIN;
        prevWasSeparator = false;
        seenText = true;
        return;
      }
      if (isZeroLength(block.style.marginBottom)) {
        // Either nothing is declared — so a mail client would apply its own 1em
        // — or it is one of Word's and Docs' scaffolding zeroes, which leaned on
        // the blank paragraph we just folded away. Both want our gap.
        // Left alone when it already says this: writing it again would expand
        // `margin: 0 0 0.75em 0` into longhands and the render would stop being
        // stable, which every other pass here is careful about.
        const top = block.style.marginTop;
        if (!((top === '0px' || top === '0') && block.style.marginBottom === PARAGRAPH_MARGIN)) {
          block.style.marginTop = '0';
          block.style.marginBottom = PARAGRAPH_MARGIN;
        }
      } else if (!block.style.marginTop) {
        // A gap set on purpose is the writer's, not ours. Only pin the top, so
        // the client's default 1em cannot stack on top of their measurement.
        block.style.marginTop = '0';
      }
      prevWasSeparator = false;
      seenText = true;
      return;
    }
    // A blank line already decided on an earlier pass stays exactly as it is.
    if (block.hasAttribute(KEPT_GAP)) { prevWasSeparator = true; return; }
    // The first blank of a run is the gap the margin now draws; a second one is
    // an empty line the writer meant. Blanks before any text go outright.
    if (!seenText || !prevWasSeparator) {
      doomed.push(block);
    } else {
      block.setAttribute(KEPT_GAP, '1');
      block.style.margin = '0';
    }
    prevWasSeparator = true;
  });
  doomed.forEach(block => block.remove());
};

/**
 * Make a pasted body survive a mail client.
 *
 * Two things a browser renders correctly and Outlook, Gmail and Apple Mail do
 * not — both of which showed up in real sent mail:
 *
 * 1. `white-space: pre-wrap`. Google Docs (and Word's web export) puts several
 *    paragraphs inside ONE <span> separated by literal newlines, and leans on
 *    pre-wrap to show them as separate lines. Mail clients drop that
 *    declaration, the newlines collapse to single spaces, and the whole pitch
 *    arrives as one run-on paragraph — "Hello there, At American Green
 *    Solutions (AGS), we support…". Newlines become real <br>s here, which
 *    every client honours.
 *
 * 2. `<li><p>…</p></li>`. A block paragraph inside a list item takes the
 *    client's default paragraph margin, which opens two blank lines around
 *    every bullet, and in several clients suppresses the bullet marker
 *    altogether. Unwrapping the paragraph puts the text back on the list item
 *    where the marker belongs.
 *
 * Idempotent: no newlines and no wrapped paragraphs are left afterwards, so a
 * second pass changes nothing.
 */
const PRE_WHITESPACE = /^pre(-wrap|-line)?$/i;

const breakOutPreWhitespace = (root) => {
  if (!root) return;
  root.querySelectorAll('[style*="white-space"]').forEach(el => {
    if (el.tagName === 'PRE' || el.tagName === 'CODE') return;
    if (!PRE_WHITESPACE.test((el.style.whiteSpace || '').trim())) return;
    // Collect first: the walker cannot survive us rewriting the tree under it.
    const doc = el.ownerDocument;
    const walker = doc.createTreeWalker(el, 4 /* SHOW_TEXT */);
    const textNodes = [];
    let node = walker.nextNode();
    while (node) { if (node.nodeValue && node.nodeValue.includes('\n')) textNodes.push(node); node = walker.nextNode(); }
    textNodes.forEach(text => {
      const parts = text.nodeValue.split('\n');
      const fragment = doc.createDocumentFragment();
      parts.forEach((part, i) => {
        if (i > 0) fragment.appendChild(doc.createElement('br'));
        if (part) fragment.appendChild(doc.createTextNode(part));
      });
      text.parentNode.replaceChild(fragment, text);
    });
    // The declaration has done its job; leaving it behind would also stop the
    // text wrapping at the reader's window width.
    el.style.whiteSpace = 'normal';
  });
};

const unwrapParagraphsInListItems = (root) => {
  if (!root) return;
  root.querySelectorAll('li').forEach(li => {
    Array.from(li.children).forEach(child => {
      if (child.tagName !== 'P' && child.tagName !== 'DIV') return;
      if (child.querySelector('ul, ol, table, img')) {
        // Too much inside to flatten safely — just take the margin off.
        child.style.margin = '0';
        return;
      }
      // Keep the paragraph's own look on the list item, then drop the block.
      const style = child.getAttribute('style');
      if (style && !li.getAttribute('style')) li.setAttribute('style', style);
      child.replaceWith(...child.childNodes);
    });
  });
};

export const normaliseForMailIn = (root) => {
  breakOutPreWhitespace(root);
  unwrapParagraphsInListItems(root);
};

/**
 * Scrub the app chrome that comes along when you copy out of Word Online,
 * Google Docs or Teams.
 *
 * Copying text that is SELECTED in Word Online brings the selection itself with
 * it: every paragraph ends with
 *   <span class="EOP Selected" style="... background-color: rgb(198,198,198)
 *   !important; border-color: rgb(198,198,198) !important;">&nbsp;</span>
 * — a grey end-of-paragraph marker. Delivered, that reads as a highlight behind
 * the whole message, which is why sent mail looked like the text was still
 * selected. Real templates here carry 20-27 of them each.
 *
 * The same paste also drags in live UI: <button class="fui-Button"> controls
 * from the editor toolbar, and a pile of properties that only mean anything to
 * a browser (user-select, cursor, -webkit-*, transition, font-kerning).
 *
 * Deliberate formatting is untouched: a highlight the writer actually applied
 * has no selection class and is not on an empty element, so it survives.
 */
const SELECTION_CLASS = /(^|\s)(EOP|Selected|SelectionHighlight|CursorOverlay)(\s|$)/i;

// Meaningless in an email, and they are what make a paste look "live".
const BROWSER_ONLY_PROPS = [
  'user-select', '-webkit-user-select', '-webkit-user-drag',
  '-webkit-tap-highlight-color', '-webkit-touch-callout',
  'cursor', 'font-kerning', 'transition', 'transition-duration',
  'transition-property', 'transition-timing-function',
  'outline-style', 'outline', 'caret-color', 'pointer-events'
];

// Live controls copied out of the editor's own toolbar. Never email content.
const APP_UI_TAGS = 'button, input, select, textarea, [role="button"], [contenteditable="true"]';

const isBlankElement = (el) => !el.querySelector('img, table, svg')
  && !el.textContent.replace(/ /g, ' ').trim();

/**
 * Word Online's paragraph scaffolding, made to behave in an inbox.
 *
 * Word Online emits every paragraph as
 *     <div class="OutlineElement" style="clear:both;position:relative">
 *       <p class="Paragraph">…</p>
 *     </div>
 * and zeroes `p.Paragraph`'s margin in its OWN stylesheet — which is not part of
 * what gets copied. In a mail client that paragraph therefore falls back to the
 * default 1em above AND below, on top of the empty paragraph the writer typed
 * between sections. One intended gap arrives as roughly three.
 *
 * So: drop the wrapper divs (pure layout scaffolding — `clear:both` and
 * `position:relative` mean nothing in email), and state the margin Word was
 * already applying. The result is the spacing the writer saw when they wrote it.
 */
const WORD_WRAPPER = /(^|\s)(OutlineElement|ListContainerWrapper)(\s|$)/;
const WORD_PARAGRAPH = /(^|\s)Paragraph(\s|$)/;

const normaliseWordOnlineSpacing = (root) => {
  if (!root) return;

  // The wrappers only ever hold one block; unwrap them.
  root.querySelectorAll('div[class]').forEach(div => {
    if (!WORD_WRAPPER.test(div.getAttribute('class') || '')) return;
    div.replaceWith(...div.childNodes);
  });

  // Restate the margin Word Online applied through its stylesheet.
  root.querySelectorAll('p[class]').forEach(p => {
    if (!WORD_PARAGRAPH.test(p.getAttribute('class') || '')) return;
    const style = p.getAttribute('style') || '';
    if (/margin/i.test(style)) return;      // an explicit margin is respected
    p.style.margin = '0';
  });
};

export const stripPastedAppChromeIn = (root) => {
  if (!root) return;
  normaliseWordOnlineSpacing(root);

  // 1. Live UI controls go entirely — keep any text that was inside them.
  root.querySelectorAll(APP_UI_TAGS).forEach(el => {
    if (el === root) return;
    if (el.tagName === 'BUTTON' || el.tagName === 'INPUT'
      || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
      el.remove();
    } else {
      el.removeAttribute('role');
      el.removeAttribute('contenteditable');
    }
  });

  root.querySelectorAll('[style]').forEach(el => {
    BROWSER_ONLY_PROPS.forEach(prop => {
      try { el.style.removeProperty(prop); } catch { /* ignore */ }
    });

    const cls = el.getAttribute('class') || '';
    const bg = (el.style.backgroundColor || '').trim().toLowerCase();

    // 2. The selection highlight itself, in all three forms it arrives in.
    const isSelectionArtifact = SELECTION_CLASS.test(cls)
      // An EMPTY element with a background is a paragraph-end marker, never a
      // highlight somebody meant to apply to their words.
      || (bg && bg !== 'transparent' && isBlankElement(el));

    if (isSelectionArtifact) {
      el.style.removeProperty('background-color');
      el.style.removeProperty('background');
      el.style.removeProperty('border-color');
    } else if (bg === 'transparent') {
      // Does nothing visually, and mail clients differ on how they read it.
      el.style.removeProperty('background-color');
    }

    if (!el.getAttribute('style')) el.removeAttribute('style');
  });
};

export const stripPastedAppChrome = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return html;
  stripPastedAppChromeIn(root);
  return root.innerHTML;
};

/**
 * The AGS social badges link themselves.
 *
 * The LinkedIn and Instagram icons in the signature are inline images, so there
 * is nothing for a recipient to click unless someone remembers to wrap each one
 * in a link — in every template, every time the signature is re-pasted. These
 * two are known quantities, so they are linked automatically on the way out.
 *
 * Change an address here and every template follows; there is nothing to edit
 * template by template.
 */
export const SOCIAL_LINKS = {
  linkedin: 'https://www.linkedin.com/company/amgsol/posts/?feedView=all',
  instagram: 'https://www.instagram.com/americangreensolutions24/'
};

/**
 * The badges we already know byte for byte.
 *
 * Matching an exact base64 payload — length plus first and last characters —
 * cannot mistake a different picture for a logo, so this is the first and
 * cheapest thing tried, and it is the only recognition that works without
 * decoding the image (`renderEmailBody` is synchronous).
 *
 * What it CANNOT do is recognise a badge nobody has registered yet: re-pasting,
 * re-encoding or resizing a logo makes a new payload every time, and that is
 * why signatures kept arriving with dead badges. Recognition by look —
 * `linkSocialLogosByLook` in utils/emailImages.js — is the answer to that; this
 * list is the fast path, and every encoding below was taken from a real stored
 * template. `describeSocialLogo` prints the line for anything new.
 */
export const SOCIAL_LOGO_SIGNATURES = [
  // LinkedIn
  { network: 'linkedin', len: 2460, head: 'iVBORw0KGgoAAAANSUhEUgAAADYAAAA2CAYAAACMRWrdAAAG', tail: 'QVQDAGra58IGsgpYAAAAAElFTkSuQmCC' },     // 54x54  Siva / shared Initial Sales Pitch
  { network: 'linkedin', len: 2548, head: 'iVBORw0KGgoAAAANSUhEUgAAADYAAAA2CAYAAACMRWrdAAAH', tail: 'VAMARUh2mg2euzMAAAAASUVORK5CYII=' },     // 54x54  Pragathi / Follow-up 1, BESS
  { network: 'linkedin', len: 1904, head: 'R0lGODdhJQAlAHcAACH+GlNvZnR3YXJlOiBNaWNyb3NvZnQg', tail: 'pqvuuuhuwslAnRiCCEP01mvvvQUFBAA7' },     // 37x37  Hima (GIF)
  { network: 'linkedin', len: 1780, head: 'iVBORw0KGgoAAAANSUhEUgAAACUAAAAlCAYAAADFniADAAAE', tail: 'VAMAadju1UWST1YAAAAASUVORK5CYII=' },     // 37x37  Pragathi
  { network: 'linkedin', len: 1388, head: 'iVBORw0KGgoAAAANSUhEUgAAACUAAAAlCAMAAADyQNAxAAAA', tail: 'C4kBAGGEtaMCbg6QAAAAAElFTkSuQmCC' },     // 37x37  Hima
  { network: 'linkedin', len: 2004, head: 'iVBORw0KGgoAAAANSUhEUgAAAC0AAAAtCAYAAAA6GuKaAAAF', tail: 'VAMAUBPz72wrNHIAAAAASUVORK5CYII=' },     // 45x45  Rithika
  { network: 'linkedin', len: 4840, head: 'iVBORw0KGgoAAAANSUhEUgAAAHgAAAB4CAYAAAA5ZDbSAAAA', tail: '/wMKOQzP8ICwOAAAAABJRU5ErkJggg==' },     // 120x120 Surabhi
  { network: 'linkedin', len: 26804, head: 'iVBORw0KGgoAAAANSUhEUgAAAgAAAAIACAYAAAD0eNT6AAAQ', tail: 'QVQDAOc5hP+knX4yAAAAAElFTkSuQmCC' },    // 512x512 Janvi
  { network: 'linkedin', len: 34104, head: 'iVBORw0KGgoAAAANSUhEUgAAAgAAAAIACAYAAAD0eNT6AAAQ', tail: 'QVQDACv0dirRQo/7AAAAAElFTkSuQmCC' },    // 512x512 shared stage templates
  // Instagram
  { network: 'instagram', len: 5512, head: 'iVBORw0KGgoAAAANSUhEUgAAAEEAAAAxCAYAAACF1cSEAAAP', tail: 'VAMATr87yKqmWPcAAAAASUVORK5CYII=' },    // 65x49  Siva / shared Initial Sales Pitch
  { network: 'instagram', len: 6012, head: 'iVBORw0KGgoAAAANSUhEUgAAAEEAAAAxCAYAAACF1cSEAAAQ', tail: 'VAMAEuq1zONndWoAAAAASUVORK5CYII=' },    // 65x49  Pragathi / Follow-up 1, BESS
  { network: 'instagram', len: 2052, head: 'R0lGODdhJQAlAHcAACH+GlNvZnR3YXJlOiBNaWNyb3NvZnQg', tail: '65Y/AehzsQw544w00jcvrYHAIgsUEAA7' },    // 37x37  Hima (GIF)
  { network: 'instagram', len: 2524, head: 'iVBORw0KGgoAAAANSUhEUgAAACUAAAAlCAYAAADFniADAAAH', tail: 'VAMA2gK5CbDUS6gAAAAASUVORK5CYII=' },    // 37x37  Pragathi
  { network: 'instagram', len: 1224, head: 'iVBORw0KGgoAAAANSUhEUgAAACUAAAAlCAMAAADyQNAxAAAA', tail: '9B/dd83nLe0fygAAAABJRU5ErkJggg==' },    // 37x37  Hima
  { network: 'instagram', len: 4732, head: 'iVBORw0KGgoAAAANSUhEUgAAAC0AAAAtCAYAAAA6GuKaAAAN', tail: 'AwABlGJWIQfhwwAAAABJRU5ErkJggg==' },    // 45x45  Rithika
  { network: 'instagram', len: 11216, head: 'iVBORw0KGgoAAAANSUhEUgAAAHgAAAB4CAYAAAA5ZDbSAAAA', tail: 'm6r/B9XDfTuDz4egAAAAAElFTkSuQmCC' },   // 120x120 Surabhi
  { network: 'instagram', len: 6132, head: '/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUA', tail: 'FILRjx50nVNbNUm8h8OZfeCCCDEKv//Z' },    // 120x120 Janvi (JPEG)
  { network: 'instagram', len: 36072, head: 'iVBORw0KGgoAAAANSUhEUgAAAHgAAAB4CAYAAAA5ZDbSAAAQ', tail: 'VAMANPB5m+XP/zsAAAAASUVORK5CYII=' }    // 120x120 shared stage templates
];

/**
 * The attribute a badge carries once it has been recognised by LOOK.
 *
 * The exact-payload list above only knows the encodings that existed when it
 * was written, and a signature that is re-pasted, re-encoded or resized makes a
 * new one every time — which is why badges kept arriving unclickable. The
 * picture itself does not change, so `linkSocialLogosByLook` (emailImages.js)
 * compares the decoded pixels instead, and marks what it finds with this
 * attribute. From then on recognition is instant and needs no decoding.
 */
export const SOCIAL_LOGO_ATTR = 'data-ags-social';

const base64Of = (src) => {
  const i = String(src || '').indexOf(';base64,');
  return i < 0 ? '' : src.slice(i + 8);
};

/** Which network this image is, or null. */
export const socialNetworkOf = (img) => {
  if (!img || !img.getAttribute) return null;
  // Already recognised by look, and the answer was written onto the tag.
  const marked = img.getAttribute(SOCIAL_LOGO_ATTR);
  if (marked && SOCIAL_LINKS[marked]) return marked;
  const payload = base64Of(img.getAttribute('src'));
  if (!payload) return null;
  const hit = SOCIAL_LOGO_SIGNATURES.find(sig =>
    payload.length === sig.len
    && payload.startsWith(sig.head)
    && payload.endsWith(sig.tail));
  return hit ? hit.network : null;
};

/**
 * Print the registry line for a logo that is not recognised yet, so a newly
 * pasted badge takes one paste of console output to support.
 */
export const describeSocialLogo = (img) => {
  const payload = base64Of(img && img.getAttribute && img.getAttribute('src'));
  if (!payload) return null;
  return `{ network: '?', len: ${payload.length}, head: '${payload.slice(0, 48)}', tail: '${payload.slice(-32)}' },`;
};

export const autoLinkSocialLogosIn = (root) => {
  if (!root) return 0;
  let linked = 0;
  root.querySelectorAll('img').forEach(img => {
    const network = socialNetworkOf(img);
    if (!network) return;
    const url = SOCIAL_LINKS[network];
    if (!url) return;
    // A link somebody set by hand wins — never overwrite a deliberate choice.
    if (imageLinkOf(img)) return;
    linkImage(img, url);
    linked += 1;
  });
  return linked;
};

export const DEFAULT_BODY_STYLE = {
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '14px',
  lineHeight: '1.5',
  textColor: '#333333'
};

const escapeForEmail = (text) => String(text == null ? '' : text)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * THE one way an email body becomes HTML.
 *
 * Every surface uses this — the Templates page, the Send Emails composer and
 * the message that actually goes out — so all three are the same markup by
 * construction rather than by three lots of similar code drifting apart. That
 * drift is exactly why a template could look right and the sent mail wrong.
 *
 * Content that carries its own formatting (anything pasted) is passed through
 * untouched apart from three safety passes: editor chrome removed, unsized
 * images capped, list markers spelled out for mail clients. Plain typed text —
 * which has no styling of its own — gets our default font and paragraphs.
 *
 * Idempotent: rendering an already-rendered body returns it unchanged.
 */
// Rendering the 1.7 MB pitch means parsing it three times — about 400ms — and
// the editors re-render on every keystroke and state change. The input is a
// string and the output is pure, so remember the last few results. Bounded so a
// long editing session cannot grow it without limit.
const RENDER_CACHE = new Map();
const RENDER_CACHE_LIMIT = 24;

// A cheap content hash, NOT the text itself.
//
// The key used to embed the whole body, so every cache probe allocated a fresh
// copy of it. With a 4 MB template on 29 rows on screen that was ~116 MB of
// throwaway string on EVERY re-render, and the tab died. The hash walks the
// string without copying it, and the length + endpoints make a collision
// between two real templates vanishingly unlikely.
const hashText = (text) => {
  let h = 5381;
  // Sampling keeps this O(4k) instead of O(4 MB) — with the length and the
  // head/tail also in the key, two different bodies colliding is not realistic.
  const step = Math.max(1, Math.floor(text.length / 4096));
  for (let i = 0; i < text.length; i += step) h = (((h << 5) + h) ^ text.charCodeAt(i)) >>> 0;
  return h.toString(36);
};
const cacheKey = (text, style) => [
  style?.fontFamily || '', style?.fontSize || '', style?.lineHeight || '', style?.textColor || '',
  text.length, hashText(text), text.slice(0, 64), text.slice(-64)
].join('|');

export const renderEmailBody = (body, style = DEFAULT_BODY_STYLE) => {
  const text = typeof body === 'string' ? body : '';
  const isHtml = text.includes('<');

  if (isHtml) {
    const key = cacheKey(text, style);
    const cached = RENDER_CACHE.get(key);
    if (cached !== undefined) return cached;

    // One parse for all three passes. Parsing this body costs ~170ms, so doing
    // it three times was most of the render.
    let rendered;
    const needsWork = /resize-handle|image-wrapper|contenteditable/i.test(text)
      || /<img/i.test(text) || /<(ul|ol|li)\b/i.test(text)
      // A body saved before the spacing fix carries our old double gap.
      || /0\.5em/.test(text)
      // Google Docs / Word web paste: newlines held up by pre-wrap alone.
      || /white-space/i.test(text)
      // Tailwind's own variables, pasted in from a rendered page — a quarter of
      // one real template, and enough to make Gmail clip the message.
      || text.includes('--tw-')
      // Word Online selection highlight, live UI copied along with it, and the
      // paragraph scaffolding that doubles every gap.
      || /background|<button|user-select|-webkit-|OutlineElement|ListContainerWrapper/i.test(text)
      // Any body with paragraphs at all needs the rhythm pass: the gaps between
      // them are what a template mixes up. The render cache pays for it once.
      || /<(p|div)\b/i.test(text)
      // No surface wrapper yet, so the message is still relying on whatever
      // font the screen it lands on happens to use.
      || !text.includes(BODY_MARK);
    if (!needsWork) {
      rendered = text;
    } else {
      const doc = new DOMParser().parseFromString(`<div id="root">${text}</div>`, 'text/html');
      const root = doc.getElementById('root');
      if (!root) {
        rendered = text;
      } else {
        stripEditorChromeIn(root);
        stripEditorVariablesIn(root);
        stripPastedAppChromeIn(root);
        constrainImagesIn(root);
        autoLinkSocialLogosIn(root);
        // Before the list pass: it flattens <li><p> so the markers survive.
        normaliseForMailIn(root);
        inlineListStylesIn(root);
        collapseGeneratedSpacersIn(root);
        // Last two: they settle the gaps every other pass leaves behind, and
        // then state the typography the whole message is laid out with.
        makeReparseStableIn(root);
        normaliseBlockRhythmIn(root);
        pinNestedBlockMarginsIn(root);
        // After the gaps are settled and before the surface is written: this
        // one restates what is already decided in the units Outlook for
        // Windows can actually read.
        makeWordSafeIn(root, style);
        wrapBodySurfaceIn(root, style);
        rendered = root.innerHTML;
      }
    }
    if (RENDER_CACHE.size >= RENDER_CACHE_LIMIT) {
      RENDER_CACHE.delete(RENDER_CACHE.keys().next().value); // drop the oldest
    }
    RENDER_CACHE.set(key, rendered);
    // The rendered form is what comes back next time the editor re-reads it,
    // and rendering it again is a no-op — record that so it costs nothing.
    if (rendered !== text) RENDER_CACHE.set(cacheKey(rendered, style), rendered);
    return rendered;
  }

  const fontFamily = style.fontFamily || DEFAULT_BODY_STYLE.fontFamily;
  const fontSize = style.fontSize || DEFAULT_BODY_STYLE.fontSize;
  const lineHeight = style.lineHeight || DEFAULT_BODY_STYLE.lineHeight;
  const textColor = style.textColor || DEFAULT_BODY_STYLE.textColor;
  // Every paragraph already carries a 0.75em bottom margin, so the blank line a
  // person types BETWEEN two paragraphs is the same gap said twice — that is
  // what made sent mail arrive triple-spaced. One blank line is therefore the
  // separator the margin already draws, and only the SECOND and further blank
  // lines in a row become real empty lines. Blank lines at the very top and
  // bottom are dropped outright.
  const lines = text.split('\n');
  // Pixels and a percentage, not `0.5em` and `1.5` — Outlook for Windows reads
  // neither of those, and this is the path a plain typed template takes, so it
  // is the one that was arriving there as an unbroken wall of text.
  const mailLineHeight = asMailLineHeight(lineHeight);
  const spacer = `<p style="margin: 0 0 ${SPACER_MARGIN} 0; line-height: ${mailLineHeight};">&nbsp;</p>`;
  const out = [];
  let blankRun = 0;
  let seenText = false;
  lines.forEach(line => {
    const escaped = escapeForEmail(line.trim());
    if (!escaped) { blankRun += 1; return; }
    // Deliberate vertical space: the first blank is the paragraph gap itself.
    if (seenText) out.push(spacer.repeat(Math.max(0, blankRun - 1)));
    blankRun = 0;
    seenText = true;
    out.push(`<p style="margin: 0 0 ${PARAGRAPH_MARGIN} 0; line-height: ${mailLineHeight};">${escaped}</p>`);
  });
  const paragraphs = out.join('');
  // Marked as the surface, so re-rendering this (it is HTML now) recognises it
  // rather than wrapping it a second time.
  return `<div ${BODY_MARK}="1" style="font-family: ${fontFamily}; font-size: ${fontSize}; line-height: ${mailLineHeight}; color: ${textColor};">${paragraphs}</div>`;
};

/**
 * Strip editor chrome before a body is saved or sent.
 *
 * The template editor makes images draggable by wrapping each one in a
 * .image-wrapper and hanging eight .resize-handle squares off it. Those are
 * absolutely-positioned green boxes — fine on screen, but they get serialised
 * into innerHTML along with the content. Mail clients drop `position:absolute`,
 * so every handle lands in the flow and the signature arrives with columns of
 * green squares under each logo. The editor re-creates the handles from the
 * images whenever it loads, so removing them here loses nothing.
 */
/**
 * Throw away the editor's own CSS variables.
 *
 * Copying out of a rendered email — including out of THIS app — brings the
 * browser's computed styles with it, and Tailwind declares its custom
 * properties (`--tw-translate-x`, `--tw-border-spacing-y`, and sixty more) on
 * every element. Pasted back in, one signature carried 6,966 of them: 140 KB of
 * a 561 KB template, a quarter of the message, and not one of them means
 * anything to a mail client.
 *
 * Size is not cosmetic here. Gmail CLIPS a message over about 102 KB — it shows
 * "[Message clipped] View entire message" and cuts the signature in half, which
 * is exactly what the team was looking at.
 *
 * Only `--custom-properties` go. Every real declaration is left untouched.
 */
export const stripEditorVariablesIn = (root) => {
  if (!root) return 0;
  let removed = 0;
  root.querySelectorAll('[style]').forEach(el => {
    const declared = el.getAttribute('style') || '';
    if (!declared.includes('--')) return;
    const kept = declared.split(';').filter(part => !/^\s*--/.test(part));
    removed += declared.split(';').length - kept.length;
    const rebuilt = kept.join(';').replace(/^[\s;]+/, '').trim();
    if (rebuilt) el.setAttribute('style', rebuilt.endsWith(';') ? rebuilt : `${rebuilt};`);
    else el.removeAttribute('style');
  });
  return removed;
};

export const stripEditorChromeIn = (root) => {
  if (!root) return;
  root.querySelectorAll('.resize-handle').forEach(el => el.remove());
  root.querySelectorAll('.image-wrapper').forEach(wrapper => {
    // A resized image keeps its size on the WRAPPER, so hand that to the image
    // before the wrapper goes — otherwise the image springs back to full size.
    const img = wrapper.querySelector('img');
    if (img) {
      if (wrapper.style.width && !img.getAttribute('width') && !img.style.width) {
        img.style.width = wrapper.style.width;
      }
      if (wrapper.style.height && !img.getAttribute('height') && !img.style.height) {
        img.style.height = wrapper.style.height;
      }
      img.style.cursor = '';
    }
    wrapper.replaceWith(...wrapper.childNodes);
  });

  // Absolute positioning does not survive a mail client: anything placed this
  // way lands wherever the flow puts it, which is how signatures ended up
  // scattered. Put those elements back in the flow here, where we can see it.
  root.querySelectorAll('[style*="absolute"]').forEach(el => {
    if ((el.style.position || '').toLowerCase() !== 'absolute') return;
    el.style.position = '';
    el.style.left = '';
    el.style.top = '';
    el.style.opacity = '';
    el.style.transition = '';
    if (!el.getAttribute('style')) el.removeAttribute('style');
  });
  root.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));

  // Undo the styling older versions of the editor stamped onto images.
  // `display:block` forces one image per line (it stacked signature icons that
  // belong side by side) and `width:100%` was only ever relative to the wrapper
  // we just removed, so it has to become the real pixel size.
  root.querySelectorAll('img').forEach(img => {
    const w = img.getAttribute('width');
    const h = img.getAttribute('height');
    if (/%/.test(img.style.width || '') && w) img.style.width = `${w}px`;
    if (/%/.test(img.style.height || '') && h) img.style.height = `${h}px`;
    // display:block put one image per line; the rest is editor decoration,
    // including the green "selected" ring.
    clearStyle(img, 'display', 'object-fit', 'cursor', 'outline');
  });
  root.querySelectorAll('[draggable]').forEach(el => el.removeAttribute('draggable'));
  // Leftover selection outlines from the drag/resize UI.
  root.querySelectorAll('[style*="dashed transparent"]').forEach(el => {
    el.style.border = '';
    if (!el.getAttribute('style')) el.removeAttribute('style');
  });
};

/** String-in, string-out wrapper — used by the Tidy button and the save paths. */
export const stripEditorChrome = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  if (!/resize-handle|image-wrapper|contenteditable|draggable|dashed transparent/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return html;
  stripEditorChromeIn(root);
  return root.innerHTML;
};

/**
 * Paste faithfully. What you copied is what you get: every inline style,
 * margin, indent, alignment, font, colour, table and image comes through
 * untouched. Only markup a browser cannot render as content is removed —
 * document wrappers, <style>/<script> blocks (a pasted <style> would restyle
 * the whole page) and Office's own `<o:p>` / `<v:*>` placeholder tags.
 *
 * Cleaning up Word's page furniture is deliberately NOT done here — it is the
 * Tidy button, so it only ever happens when you ask for it.
 */
export const preparePastedHtml = (html) => {
  if (!html || typeof html !== 'string') return '';

  // Outlook and Google Docs put layout in a <style> block and reference it by
  // class. We cannot keep the block (it would restyle the whole page), but
  // dropping it loses the layout — which is why pasted signatures came apart.
  // So: read the rules, write them onto the elements they match as inline
  // styles, then drop the block. Same look, no leakage.
  const styleBlocks = [...html.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)].map(m => m[1]).join('\n');

  const stripped = html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(style|script|xml|title)[\s\S]*?<\/\1>/gi, '')
    .replace(/<\/?(html|head|body|meta|link|base)[^>]*>/gi, '')
    .replace(/<\/?o:p[^>]*>/gi, '')
    .replace(/<\/?v:[^>]*>/gi, '');

  if (!styleBlocks.trim()) return stripped;

  try {
    const doc = new DOMParser().parseFromString(`<div id="root">${stripped}</div>`, 'text/html');
    const root = doc.getElementById('root');
    if (!root) return stripped;

    // Simple `selector { declarations }` rules only. @media / @font-face and
    // anything else exotic is skipped rather than guessed at.
    const cleanCss = styleBlocks.replace(/\/\*[\s\S]*?\*\//g, '').replace(/@[a-z-]+[^{]*\{[\s\S]*?\}\s*\}/gi, '');
    const ruleRe = /([^{}]+)\{([^{}]+)\}/g;
    let rule;
    while ((rule = ruleRe.exec(cleanCss)) !== null) {
      const selectors = rule[1].split(',').map(x => x.trim()).filter(Boolean);
      const declarations = rule[2].trim();
      if (!declarations) continue;
      selectors.forEach(selector => {
        if (/[:@]/.test(selector)) return; // pseudo-classes cannot be inlined
        let matches = [];
        try { matches = [...root.querySelectorAll(selector)]; } catch { return; }
        matches.forEach(el => {
          // The rule goes UNDER anything already inline — inline wins, as in CSS.
          const existing = el.getAttribute('style') || '';
          el.setAttribute('style', `${declarations};${existing}`);
        });
      });
    }
    return root.innerHTML;
  } catch {
    return stripped;
  }
};

/**
 * Clean HTML that came out of Word / Outlook / Google Docs. Runs ONLY from the
 * Tidy button — never automatically on paste.
 *
 * Those apps wrap content in page furniture that looks nothing like the source
 * once it lands in a web editor: `mso-element:para-border-div` boxes (the stray
 * vertical rules), a WordSection div with a fixed 8.5in page width (content
 * ends up in a narrow, zoomed-out column), `zoom:` declarations, and runs of
 * empty spacer paragraphs. Everything that carries real meaning — bold, italic,
 * colour, font, size, alignment, lists, links, images, tables — is kept exactly
 * as pasted.
 */
const STYLE_DROP = [
  /^mso-/i,                         // every Word-only declaration
  /^border/i,                       // para-border boxes => stray vertical lines
  /^width$/i, /^min-width$/i, /^max-width$/i,   // page widths => narrow column
  /^height$/i, /^min-height$/i,
  /^zoom$/i, /^transform$/i,        // Outlook's zoom => "zoomed out" text
  /^position$/i, /^float$/i, /^clear$/i,
  /^page/i, /^layout-grid/i, /^text-justify$/i,
  /^background(-color)?$/i,         // white/transparent blocks over our own bg
  /^tab-stops$/i, /^punctuation-wrap$/i, /^word-break$/i
];
const KEEP_TAGS = new Set([
  'A','B','STRONG','I','EM','U','S','STRIKE','SUP','SUB','BR','P','DIV','SPAN',
  'UL','OL','LI','H1','H2','H3','H4','H5','H6','BLOCKQUOTE','PRE','CODE','HR',
  'IMG','TABLE','THEAD','TBODY','TFOOT','TR','TD','TH','FONT','CENTER','LABEL'
]);

const cleanStyle = (el, insideTable) => {
  const style = el.getAttribute('style');
  if (!style) return;
  const kept = [];
  style.split(';').forEach(decl => {
    const idx = decl.indexOf(':');
    if (idx < 0) return;
    const prop = decl.slice(0, idx).trim();
    const val = decl.slice(idx + 1).trim();
    if (!prop || !val) return;
    // Table cells legitimately need their borders, widths and backgrounds.
    const isTablePart = insideTable && /^(TABLE|TD|TH|TR|THEAD|TBODY|TFOOT)$/.test(el.tagName);
    if (!isTablePart && STYLE_DROP.some(rx => rx.test(prop))) return;
    if (/^mso-/i.test(val)) return;
    kept.push(`${prop}: ${val}`);
  });
  if (kept.length) el.setAttribute('style', kept.join('; '));
  else el.removeAttribute('style');
};


/** "1in" / "36.0pt" / "48px" / "2em" -> pixels. Returns 0 for anything else. */
const lengthToPx = (value) => {
  if (!value) return 0;
  const m = String(value).trim().match(/^(-?[\d.]+)\s*(px|pt|in|cm|mm|em|rem)?$/i);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  if (isNaN(n)) return 0;
  switch ((m[2] || 'px').toLowerCase()) {
    case 'pt': return n * (96 / 72);
    case 'in': return n * 96;
    case 'cm': return n * (96 / 2.54);
    case 'mm': return n * (96 / 25.4);
    case 'em':
    case 'rem': return n * 16;
    default: return n;
  }
};

/**
 * Word pastes carry the document's page margin on every paragraph, so the whole
 * email sits pushed in from the left. Shift everything back by the smallest
 * indent in the content: the body lands flush left and the relative indents
 * (sub-points under a heading) are preserved exactly. List items are left alone
 * — their indentation belongs to the <ul>/<ol>.
 */
const removeInheritedIndent = (root) => {
  const BLOCKS = 'p, div, h1, h2, h3, h4, h5, h6, blockquote';

  // A container that only wraps other blocks is page furniture — its indent is
  // the document margin, never something the writer typed.
  Array.from(root.querySelectorAll(BLOCKS)).forEach(el => {
    if (el.querySelector(BLOCKS)) {
      el.style.marginLeft = '';
      el.style.paddingLeft = '';
      el.style.marginRight = '';
      el.style.paddingRight = '';
      if (!el.getAttribute('style')) el.removeAttribute('style');
    }
  });

  // Then shift the actual text blocks back by the smallest indent among them,
  // so the body sits flush left and relative indents (sub-points under a
  // heading) survive untouched. List items keep the <ul>/<ol> indentation.
  const blocks = Array.from(root.querySelectorAll(BLOCKS))
    .filter(el => !el.querySelector(BLOCKS))
    .filter(el => !el.closest('li, ul, ol, table'))
    .filter(el => el.textContent.replace(/\u00a0/g, ' ').trim());
  if (!blocks.length) return;

  const leftOf = (el) => lengthToPx(el.style.marginLeft) + lengthToPx(el.style.paddingLeft);
  const baseline = Math.min(...blocks.map(leftOf));
  if (baseline <= 0) return;

  blocks.forEach(el => {
    const next = Math.max(0, leftOf(el) - baseline);
    el.style.paddingLeft = '';
    el.style.marginLeft = next > 0 ? `${Math.round(next)}px` : '';
    if (!el.getAttribute('style')) el.removeAttribute('style');
  });
};

/**
 * Outlook and Word often wrap a whole message in a one-cell table for layout.
 * Left in place it adds cell padding down the left edge, so unwrap it.
 */
const unwrapLayoutTables = (root) => {
  Array.from(root.querySelectorAll('table')).forEach(table => {
    const cells = table.querySelectorAll('td, th');
    if (cells.length !== 1) return;                       // a real table — keep it
    if (cells[0].querySelector('table')) return;
    table.replaceWith(...cells[0].childNodes);
  });
};

export const sanitizePastedHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  const stripped = html
    .replace(/<!--[\s\S]*?-->/g, '')                       // incl. Word's <!--[if ...]-->
    .replace(/<\/?(html|head|body|meta|link|base)[^>]*>/gi, '')
    .replace(/<(style|script|xml|title)[\s\S]*?<\/\1>/gi, '')
    .replace(/<\/?o:p[^>]*>/gi, '')
    .replace(/<\/?v:[^>]*>/gi, '');

  const doc = new DOMParser().parseFromString(`<div id="root">${stripped}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return stripped;

  root.querySelectorAll('*').forEach(el => {
    // Unknown / Office-only elements: keep the text, drop the wrapper.
    if (!KEEP_TAGS.has(el.tagName)) {
      el.replaceWith(...el.childNodes);
      return;
    }
    el.removeAttribute('class');
    el.removeAttribute('lang');
    el.removeAttribute('id');
    el.removeAttribute('dir');
    if (el.tagName !== 'TABLE' && el.tagName !== 'TD' && el.tagName !== 'TH') {
      el.removeAttribute('width');
      el.removeAttribute('height');
    }
    cleanStyle(el, !!el.closest('table'));
  });

  // A wrapper div that only exists to hold page furniture adds nothing.
  root.querySelectorAll('div').forEach(div => {
    if (!div.getAttribute('style') && div.parentElement === root && div.children.length) {
      div.replaceWith(...div.childNodes);
    }
  });

  unwrapLayoutTables(root);
  removeInheritedIndent(root);

  // Collapse runs of empty spacer paragraphs down to a single blank line.
  const isBlank = (node) => node.nodeType === 1
    && /^(P|DIV)$/.test(node.tagName)
    && !node.querySelector('img, table, hr, br + br')
    && !node.textContent.replace(/\u00a0/g, ' ').trim();
  let blanks = 0;
  Array.from(root.querySelectorAll('p, div')).forEach(node => {
    if (isBlank(node)) {
      blanks += 1;
      if (blanks > 1) node.remove();
    } else {
      blanks = 0;
    }
  });

  return root.innerHTML;
};

/**
 * Paste handler shared with the other editors in the app: a clipboard image is
 * embedded as a data URL (a `blob:` URL would be dead by the time the mail is
 * sent) and formatted content is inserted as HTML so bold, colours, links and
 * tables come through exactly as copied. Returns true when it handled the paste.
 */
export const handleRichPaste = (e, editorEl, onDone) => {
  const clipboard = e.clipboardData;
  if (!clipboard || !editorEl) return false;

  // HTML FIRST. Copying a signature or a formatted block out of Outlook puts
  // both a text/html flavour AND a bitmap on the clipboard; taking the bitmap
  // threw away all the text and formatting and pasted a single flat image.
  // Only when there is no HTML at all is the bitmap the actual content.
  const html = clipboard.getData('text/html');
  if (html && html.trim()) {
    e.preventDefault();
    // Settle the list markers HERE, not only on the way out. Pasted bullets
    // arrive either with the marker as a literal character (two dots in the
    // inbox) or with `list-style-type: none` inline (no dots in the composer,
    // dots in the inbox). Both were invisible until the mail landed, because
    // the composer and the send path were normalising at different moments.
    insertHtmlAtCaret(editorEl, inlineListStyles(preparePastedHtml(html)));
    onDone?.(editorEl.innerHTML);
    return true;
  }

  const items = Array.from(clipboard.items || []);
  const imageItem = items.find(i => i.type && i.type.startsWith('image/'));
  if (imageItem) {
    const file = imageItem.getAsFile();
    if (file) {
      e.preventDefault();
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result;
        if (!dataUrl) return;
        insertHtmlAtCaret(editorEl, `<img src="${dataUrl}" style="max-width:100%;height:auto;" />`);
        onDone?.(editorEl.innerHTML);
      };
      reader.readAsDataURL(file);
      return true;
    }
  }
  return false;
};

const plainToHtml = (text) => (text || '')
  .split(/\n/)
  .map(line => (line.trim() ? line : '<br>'))
  .join('<br>');

const ToolbarButton = ({ title, onRun, children, className = '' }) => (
  <button
    type="button"
    title={title}
    // onMouseDown (not onClick) so the editor keeps its selection
    onMouseDown={(e) => { e.preventDefault(); onRun(); }}
    className={`px-2 py-1 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 ${className}`}
  >
    {children}
  </button>
);

const RichEmailEditor = ({
  value,
  onChange,
  disabled = false,
  minHeight = 220,
  placeholder = '',
  style = {}
}) => {
  const editorRef = useRef(null);
  const lastSyncedRef = useRef(null);
  const fileInputRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [tableSize, setTableSize] = useState({ rows: 3, cols: 3 });
  // Click an image to select it; the toolbar then resizes it. Sizes are written
  // as pixels on the width/height attributes, which is what mail clients read.
  const [selectedImg, setSelectedImg] = useState(null);
  const [selectedWidth, setSelectedWidth] = useState('');
  // Ctrl/Cmd-click adds images to the set, so several can be lined up at once.
  const [pickedImgs, setPickedImgs] = useState([]);

  // Push value into the DOM only when it really changed, or every keystroke
  // would move the caret back to the start.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const incoming = typeof value === 'string' && value.includes('<') ? value : plainToHtml(value || '');
    if (lastSyncedRef.current === value) return;
    lastSyncedRef.current = value;
    if (el.innerHTML !== incoming) el.innerHTML = incoming;
  }, [value]);

  const emit = () => {
    const el = editorRef.current;
    if (!el) return;
    // The green selection outline is editor UI — strip it from what we store.
    const clone = el.cloneNode(true);
    clone.querySelectorAll('img').forEach(img => {
      img.style.outline = '';
      if (!img.getAttribute('style')) img.removeAttribute('style');
    });
    lastSyncedRef.current = clone.innerHTML;
    onChange?.(clone.innerHTML);
  };

  const run = (command, arg = null) => {
    if (disabled) return;
    editorRef.current?.focus();
    if (savedRangeRef.current) {
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
      } catch (_) { /* selection went stale */ }
      savedRangeRef.current = null;
    }
    document.execCommand(command, false, arg);
    emit();
  };

  const rememberSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editorRef.current?.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const insertImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result;
      if (!dataUrl) return;
      insertHtmlAtCaret(
        editorRef.current,
        `<img src="${dataUrl}" style="max-width:100%;height:auto;display:block;" />`
      );
      emit();
    };
    reader.readAsDataURL(file);
  };

  const handlePaste = (e) => {
    if (disabled) return;
    const settled = (html) => {
      lastSyncedRef.current = html;
      onChange?.(html);
      // Pasted pictures are re-encoded (and hosted, if still heavy) a beat after
      // the paste itself, so a signature of any weight can be pasted and saved.
      // Imported here rather than at the top: utils/emailImages imports this
      // module for the paste handler it wraps.
      import('../utils/emailImages')
        .then(m => m.slimEditorImages(editorRef.current, (tidied) => {
          lastSyncedRef.current = tidied;
          onChange?.(tidied);
        }))
        .catch(() => {});
    };
    const handled = handleRichPaste(e, editorRef.current, settled);
    // Plain text falls through to the browser's own handling.
    if (!handled) setTimeout(emit, 0);
  };

  const clearPicked = (keep) => {
    pickedImgs.forEach(i => { if (i !== keep) i.style.outline = ''; });
    setPickedImgs([]);
  };

  const selectImage = (img, additive = false) => {
    if (additive && img) {
      // Build up a set; each stays outlined so you can see what is included.
      img.style.outline = '2px solid #679632';
      setPickedImgs(prev => (prev.includes(img) ? prev : [...prev, img]));
      setSelectedImg(img);
      setSelectedWidth(String(
        parseInt(img.getAttribute('width'), 10) || parseInt(img.style.width, 10) || img.clientWidth || img.naturalWidth || ''
      ));
      return;
    }
    clearPicked(img);
    if (selectedImg && selectedImg !== img) selectedImg.style.outline = '';
    if (!img) {
      setSelectedImg(null);
      setSelectedWidth('');
      return;
    }
    img.style.outline = '2px solid #679632';
    setSelectedImg(img);
    const current = parseInt(img.getAttribute('width'), 10)
      || parseInt(img.style.width, 10)
      || img.clientWidth
      || img.naturalWidth;
    setSelectedWidth(String(Math.round(current || 0)));
  };

  const applyImageWidth = (px) => {
    if (!selectedImg) return;
    const applied = setImageWidth(selectedImg, px);
    if (applied) setSelectedWidth(String(applied));
    emit();
  };

  const arrange = (action) => {
    const img = selectedImg;
    const root = editorRef.current;
    if (!img || !root) return;
    if (action === 'left' || action === 'center' || action === 'right') alignImage(img, action, root);
    if (action === 'join') joinPreviousLine(img, root);
    if (action === 'own') moveToOwnLine(img, root);
    if (action === 'moveLeft' || action === 'moveRight') {
      moveImage(img, action === 'moveLeft' ? 'left' : 'right', root);
    }
    if (action === 'row') {
      const chosen = pickedImgs.length >= 2 ? pickedImgs : imagesInSelection(root);
      const count = chosen.length >= 2
        ? makeRowFromImageList(chosen, root)
        : makeRowFromImage(img, root);
      chosen.forEach(i => { i.style.outline = ''; });
      setPickedImgs([]);
      if (count < 2) {
        window.alert('Pick the images first: drag across them, or Ctrl/Cmd-click each one — then press Make a row. Clicking a single image lines it up with the image-only lines below it.');
      }
    }
    emit();
  };

  const scaleSelected = (factor) => {
    if (!selectedImg) return;
    const current = parseInt(selectedWidth, 10) || selectedImg.clientWidth || selectedImg.naturalWidth;
    applyImageWidth(Math.round(current * factor));
  };

  const insertTable = () => {
    insertHtmlAtCaret(editorRef.current, buildTableHtml(tableSize.rows, tableSize.cols));
    emit();
    setShowTablePicker(false);
  };

  // Linking a PICTURE and linking TEXT are two different operations.
  //
  // `document.execCommand('createLink')` works on a text selection and does
  // nothing at all to an image — no error, no change. That is why a BDE could
  // select the LinkedIn badge, press this button, type the address and still
  // send a signature nobody could click: the toolbar quietly ignored them.
  // With an image selected we wrap it in the anchor instead, which is the one
  // thing every mail client understands. An empty box removes the link.
  const addLink = () => {
    rememberSelection();
    if (selectedImg) {
      const current = imageLinkHref(selectedImg);
      const url = window.prompt('Link this image to (clear the box to remove the link):', current || '');
      if (url === null) return;
      linkImage(selectedImg, url);
      emit();
      return;
    }
    const url = window.prompt('Enter URL:');
    if (!url) return;
    run('createLink', url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {!disabled && (
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-2 bg-gray-50 border-b border-gray-200">
          <select
            title="Font"
            onMouseDown={rememberSelection}
            onChange={(e) => run('fontName', e.target.value)}
            defaultValue=""
            className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
          >
            <option value="" disabled>Font</option>
            {EDITOR_FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select
            title="Text size"
            onMouseDown={rememberSelection}
            onChange={(e) => {
              // execCommand fontSize only takes 1-7, so set the real px afterwards
              run('fontSize', '7');
              const el = editorRef.current;
              el?.querySelectorAll('font[size="7"]').forEach(node => {
                node.removeAttribute('size');
                node.style.fontSize = e.target.value;
              });
              emit();
            }}
            defaultValue=""
            className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
          >
            <option value="" disabled>Size</option>
            {EDITOR_SIZES.map(s => <option key={s} value={s}>{s.replace('px', '')}</option>)}
          </select>
          <input
            type="color"
            title="Text colour"
            onMouseDown={rememberSelection}
            onChange={(e) => run('foreColor', e.target.value)}
            className="w-8 h-7 border border-gray-300 rounded bg-white cursor-pointer"
          />
          <span className="w-px h-5 bg-gray-300 mx-0.5" />
          <ToolbarButton title="Bold" onRun={() => run('bold')} className="font-bold">B</ToolbarButton>
          <ToolbarButton title="Italic" onRun={() => run('italic')} className="italic">I</ToolbarButton>
          <ToolbarButton title="Underline" onRun={() => run('underline')} className="underline">U</ToolbarButton>
          <span className="w-px h-5 bg-gray-300 mx-0.5" />
          <ToolbarButton title="Align left" onRun={() => run('justifyLeft')}>L</ToolbarButton>
          <ToolbarButton title="Align centre" onRun={() => run('justifyCenter')}>C</ToolbarButton>
          <ToolbarButton title="Align right" onRun={() => run('justifyRight')}>R</ToolbarButton>
          <ToolbarButton title="Bulleted list" onRun={() => run('insertUnorderedList')}>•</ToolbarButton>
          <ToolbarButton title="Numbered list" onRun={() => run('insertOrderedList')}>1.</ToolbarButton>
          <span className="w-px h-5 bg-gray-300 mx-0.5" />
          <ToolbarButton title="Insert link — select text, or click a picture first to link the picture" onRun={addLink} className="text-blue-600">Link</ToolbarButton>
          <ToolbarButton title="Insert image" onRun={() => fileInputRef.current?.click()}>Image</ToolbarButton>
          <div className="relative">
            <ToolbarButton title="Insert table" onRun={() => setShowTablePicker(v => !v)}>Table</ToolbarButton>
            {showTablePicker && (
              <div className="absolute z-20 mt-1 p-2 bg-white border border-gray-300 rounded shadow-lg flex items-center gap-2">
                <input
                  type="number" min="1" max="20" value={tableSize.rows}
                  onChange={(e) => setTableSize(s => ({ ...s, rows: Math.max(1, Number(e.target.value) || 1) }))}
                  className="w-14 border border-gray-300 rounded px-1 py-0.5 text-xs" title="Rows"
                />
                <span className="text-xs text-gray-500">×</span>
                <input
                  type="number" min="1" max="10" value={tableSize.cols}
                  onChange={(e) => setTableSize(s => ({ ...s, cols: Math.max(1, Number(e.target.value) || 1) }))}
                  className="w-14 border border-gray-300 rounded px-1 py-0.5 text-xs" title="Columns"
                />
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); insertTable(); }}
                  className="px-2 py-1 text-xs bg-[#679632] text-white rounded"
                >
                  Insert
                </button>
              </div>
            )}
          </div>
          <ToolbarButton
            title="Turn the lines of the table around the cursor on or off"
            onRun={() => {
              const root = editorRef.current;
              const table = tableFromSelection(root);
              if (!table) { window.alert('Click inside the table first.'); return; }
              setTableBorders(table, !tableHasBorders(table));
              emit();
            }}
          >Borders</ToolbarButton>
          <ToolbarButton
            title="Tidy up Word/Outlook paste — removes stray boxes, page widths and blank spacer lines, keeps your formatting"
            onRun={() => {
              const el = editorRef.current;
              if (!el) return;
              el.innerHTML = sanitizePastedHtml(el.innerHTML);
              emit();
            }}
            className="text-gray-600"
          >Tidy</ToolbarButton>
          <ToolbarButton title="Clear formatting" onRun={() => run('removeFormat')} className="text-gray-500">Clear</ToolbarButton>
          {selectedImg && (
            <span className="flex items-center gap-1.5 pl-2 ml-1 border-l border-gray-300">
              <span className="text-xs text-gray-600">
                {pickedImgs.length >= 2 ? `${pickedImgs.length} images picked · width` : 'Image width'}
              </span>
              <input
                type="number"
                min="8"
                max="2000"
                value={selectedWidth}
                onChange={(e) => setSelectedWidth(e.target.value)}
                onBlur={() => applyImageWidth(selectedWidth)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyImageWidth(selectedWidth); } }}
                className="w-16 border border-gray-300 rounded px-1 py-0.5 text-xs"
                title="Width in pixels — height follows automatically"
              />
              <span className="text-xs text-gray-500">px</span>
              <ToolbarButton title="Half the size" onRun={() => scaleSelected(0.5)}>−</ToolbarButton>
              <ToolbarButton title="Double the size" onRun={() => scaleSelected(2)}>+</ToolbarButton>
              <span className="w-px h-5 bg-gray-300 mx-0.5" />
              <ToolbarButton title="Move this image one place left" onRun={() => arrange('moveLeft')}>◀</ToolbarButton>
              <ToolbarButton title="Move this image one place right" onRun={() => arrange('moveRight')}>▶</ToolbarButton>
              <ToolbarButton title="Line this image up with the image-only lines below it, as one row" onRun={() => arrange('row')}>▤ Make a row</ToolbarButton>
              <ToolbarButton title="Move this image onto the line above — put images side by side" onRun={() => arrange('join')}>⇧ Same line</ToolbarButton>
              <ToolbarButton title="Give this image its own line" onRun={() => arrange('own')}>⇩ Own line</ToolbarButton>
              <ToolbarButton title="Align this line left" onRun={() => arrange('left')}>⇤</ToolbarButton>
              <ToolbarButton title="Centre this line" onRun={() => arrange('center')}>⇔</ToolbarButton>
              <ToolbarButton title="Align this line right" onRun={() => arrange('right')}>⇥</ToolbarButton>
              <ToolbarButton
                title="Back to the image's own size"
                onRun={() => applyImageWidth(selectedImg.naturalWidth)}
              >Reset</ToolbarButton>
              <ToolbarButton title="Deselect" onRun={() => selectImage(null)} className="text-gray-500">✕</ToolbarButton>
            </span>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) insertImageFile(file);
              e.target.value = '';
            }}
          />
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
        onClick={(e) => selectImage(
          e.target && e.target.tagName === 'IMG' ? e.target : null,
          e.ctrlKey || e.metaKey
        )}
        className={`email-body px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#679632] ${disabled ? 'bg-gray-100' : 'bg-white'}`}
        style={{ minHeight, overflowY: 'auto', ...style }}
      />
    </div>
  );
};

export default RichEmailEditor;
